/**
 * Declarative metering specs (#1878, ADR-012 §3).
 *
 * A metering spec says HOW a SKU's billable quantity is computed, as DATA
 * rather than as a `switch` arm. ADR-012 §3 fixes the algebra and it is
 * closed on purpose:
 *
 *     billable = clamp(a x <descriptor field> + b, min, max)
 *
 * with coefficients per variant, a `variant_key` template, and pricing by an
 * `(unit, variant_key)` table lookup. Nothing outside that shape is
 * expressible, so "add a tier" and "list a new unit SKU" stay data operations
 * and cannot smuggle a new billing behaviour into the tree.
 *
 * ── Round one, and what round two (this file's current state) added ───────
 *
 * Round one was a provable no-op: the two profiles that bill today
 * (`flat_call_v1`, `openai_images_unit_v1`) got BUILT-IN specs that reproduced
 * the previous per-profile `switch` byte for byte, and the gateway's
 * "can this SKU be metered" gate became "does this SKU have a legal spec".
 * `apps/gateway-worker/test/unit-metering-spec-equivalence.test.ts` pins that
 * equivalence against a frozen copy of the pre-#1878 switch, and it is the
 * file that records where round two departed from it on purpose.
 *
 * Round two (gateway half) lands three things round one held the seat for:
 *
 *   1. **`variant_key` derivation.** A spec may declare a template and the
 *      evaluator resolves it against the gateway's CANONICAL request values —
 *      self-evidence, ADR-012 §2 — never against a tenant body field.
 *   2. **ADR-012 §2's admissibility matrix, enforced by the validator.** A
 *      priced quantity and a priced tier may read SELF-evidence only; an
 *      observation (first- or third-party) is admissible only as
 *      `delivery_zeroing_field`, where it can cancel a charge and never
 *      create one. That closes the §2 known conflict structurally rather than
 *      by asking the next author to remember it.
 *   3. **The provenance repair of `openai_images_unit_v1`.** Its quantity is
 *      now the canonical image count from the request and its delivery gate is
 *      a FIRST-party count of the image payloads the gateway saw in the
 *      response body it holds — not the upstream's `tool_usage.image_gen`
 *      assertion. §2 names the first-party gate as the required first attempt;
 *      it is reachable on this wire, so the third-party fallback is not taken.
 *
 * Round two (platform half) closes what round one held seats for: the write
 * face that stores a spec in
 * `model_metering_profiles.billable_dimensions_json` and validates it 400,
 * the `pub_model_metering_profiles` projection that ships one to the gateway,
 * and ADR-012 §3's exact-match price rule — `unitRateLookupModeFor` below is
 * its single derivation and `computeUnitCostBreakdown` is where it applies.
 *
 * ── #1886 round two: the template gate is OPEN, per REQUEST ───────────────
 *
 * `KNOWN_VARIANT_KEY_FIELDS` was empty and no stored spec could declare a
 * `variant_key` template at all. It now admits `size` and `quality`, and what
 * replaced the blanket refusal is a per-request one: a placeholder is
 * admissible only on the `canonical_overwrite` egress shape, i.e. only where
 * the gateway wrote its own canonical tier into the outbound body and the
 * route is armed to honour it. Every other route — the Responses bypass, a
 * retry chain that could fall back to it, any ingress with no tier — refuses
 * with `variant_not_forwarded` at the route gate, 400 and no upstream spend.
 * See `MeteringVariantEgress` and `KNOWN_VARIANT_KEY_FIELDS`.
 *
 * ── #1838 sub-item D: `duration_seconds` and `resolution` ─────────────────
 *
 * The asynchronous task lane sells a video by the SECOND, tiered by
 * resolution, so it adds one member to each of the two vocabularies:
 * `duration_seconds` to the priced descriptors and `resolution` to the
 * variant fields. Since #1883 `duration_seconds` is TERMINAL-REPORT grade —
 * the seconds the upstream's terminal `Success` document states, not the
 * frozen order — under ADR-012 §2 as amended: billing follows delivery, and
 * the report is trusted because the supply's admission action verified it
 * refuses rather than silently substitutes. The arguments are on
 * `DESCRIPTOR_FIELD_META` and `KNOWN_VARIANT_KEY_FIELDS` respectively, and
 * the cells are pinned by test.
 *
 * NO BUILT-IN SPEC IS ADDED FOR IT, deliberately. `duration_seconds_v1` is
 * turned on by a STORED spec and its price rows — the threading that reads one
 * has landed — so listing the first per-second SKU is pure data, and a
 * deployment that has not written that data refuses at the route gate exactly
 * as it does today. A built-in would arm the profile for every SKU carrying it,
 * everywhere, with coefficients nobody chose.
 *
 * ── SPLIT SEAMS, for whoever touches this next ────────────────────────────
 *
 * This file is the largest in `packages/contracts/src` and it is over the
 * repo's 800-line guidance. It is deliberately NOT split in this change —
 * every module below would have to re-export through the barrel and the diff
 * would bury the money rules it lands — but the seams are natural and are
 * recorded here so the split is a mechanical move rather than a judgement
 * call:
 *
 *   1. THE DECLARED VOCABULARY — the lane, descriptor and variant-field
 *      unions, `DESCRIPTOR_FIELD_META`, and the two `KNOWN_*` lists. Depends
 *      on nothing else here.
 *   2. THE VALIDATOR — `validateMeteringSpec` and its private helpers, plus
 *      `MeteringSpecViolation`. Depends on (1) only.
 *   3. THE EVALUATOR AND ADMISSION PREDICATE — `admitUnitMeteringRequest`,
 *      `evaluateUnitMeteringSpec`, `resolveVariantKey`, `evaluateTerm`, and
 *      the two descriptor types. Depends on (1) only; deliberately does NOT
 *      depend on (2), which is what lets a hand-rolled spec be evaluated in
 *      tests.
 *   4. STORED-SPEC RESOLUTION — the built-in table, `resolveStoredMeteringSpec`,
 *      `unitMeteringSpecFrom` and `unitRateLookupModeFor`. The only part that
 *      depends on (2).
 *
 * The threading change has landed, so the split is now unblocked: it touches
 * (3) and (4), which is why it was held until this file stopped moving.
 */

import type { ModelProtocol } from './internal-models.ts';
import { KNOWN_MODEL_PROTOCOLS, UNIT_VARIANT_KEY_RE } from './internal-models.ts';
import type {
  MeteringProfileKind,
  UnitMeteredProfileKind,
  UnitRateLookupMode,
  UnitUsageDetails,
  UnitUsageUnit,
} from './metering.ts';
import { KNOWN_UNIT_USAGE_UNITS } from './metering.ts';

/**
 * The billing lane a spec belongs to (ADR-012 §4).
 *
 * `unit` is the only inhabited lane and the token lane is deliberately NOT a
 * member: token billing is not spec-driven, it reads the upstream's `usage`
 * through a per-protocol extractor, and giving it a lane value here would
 * invite a bridge between lanes that ADR-012 §4 forbids.
 *
 * The discriminator exists so that a THIRD lane — a commodity whose quantity
 * does not exist at submit time, e.g. realtime audio by session minute — can
 * be added without reshaping stored specs. ADR-012 §4 records the current
 * two-lane set as a closed set, explicitly not an invariant; this field is
 * the mechanical part of that promise.
 */
/**
 * ── #1937: `token` JOINS THE UNION, AND CARRIES EXACTLY ONE THING ─────────
 *
 * The paragraph above said the token lane was deliberately not a member
 * because "token billing is not spec-driven … giving it a lane value here
 * would invite a bridge between lanes". That argument is about the QUANTITY,
 * and it still stands unchanged: a token-lane spec below may not declare a
 * `quantity`, a `unit`, a `delivery_gate` or a zeroing field, and the
 * validator refuses every one of those keys on it. The amount is still the
 * upstream's `usage` through a per-protocol extractor, and nothing here can
 * move it.
 *
 * What a token-lane spec declares is the PRICE-TIER axis and nothing else:
 * one `variant_key_template`. #1937 measured the supply charging two
 * different per-token rates for one model depending on whether the order
 * carried a video input, and a price list that cannot say which tier a
 * request fell in prices both at one rate — 46/28 = 64% wrong, in a direction
 * that drifts with the traffic mix.
 *
 * Sharing the tier vocabulary rather than minting a second one is the point:
 * `resolveVariantKey`, `VARIANT_FIELD_META` and the injectivity premise on
 * `VARIANT_KEY_TEMPLATE` are one implementation for both lanes, so "which
 * tier is this request in" cannot answer differently depending on how the
 * SKU's amount happens to be computed.
 */
export type MeteringLane = 'unit' | 'token';

export const KNOWN_METERING_LANES: readonly MeteringLane[] = ['unit', 'token'] as const;

/**
 * What one metering PROFILE may sell — its lane, and the closed field sets a
 * stored spec for it may declare (#1883).
 *
 * ── THE TWO-AXIS MODEL, so the next reader does not conflate them ──────────
 *
 * A SKU's shape has two ORTHOGONAL axes:
 *
 *   - the DOWNSTREAM axis: `protocol` + metering profile — the SALES FORM.
 *     Which wire protocol the tenant is sold, and how what it serves bills.
 *     This table is that axis's whole vocabulary.
 *   - the UPSTREAM axis: the channel and its egress binding's `adapter.vendor`
 *     (`egress-config.ts`) — which upstream connection actually serves it.
 *
 * A profile therefore names a sales form, never an upstream connection.
 * `ark_video_token_v1` is the owner-confirmed example (2026-08-16): it is the
 * "Ark-official-compatible" sales form — the tenant is sold the Ark-compatible
 * task protocol and its billing model — and which vendor's deployment answers
 * it is the egress binding's business, invisible from this axis. Selling a NEW
 * form (say a third tier scheme) is a new profile kind plus one row here. That
 * is a deliberate enumeration, not a rule engine, for the same reason the spec
 * algebra itself is closed: "add a sales form" must stay a reviewable data
 * change that cannot smuggle in behaviour nobody chose.
 *
 * ── WHAT EACH ROW CLOSES ───────────────────────────────────────────────────
 *
 * `fixedVariantKeyTemplate`: the ONE `variant_key_template` a stored spec for
 * this profile may declare — the profile's tier axis, verbatim — or `null`
 * when the form sells no tiers. The owner's 2026-08-16 verdict replaced the
 * previous shape of this column, an ADMISSION SET of fields an operator could
 * pick tiers from: a profile IS a sales form, its billing shape included, so
 * "which fields does it tier on" is decided the day the form is enumerated
 * and is NOT a per-registration choice — an operator cannot answer "should
 * this SKU tier on resolution?", and a control that asks them is a control
 * that mints unmeasured price structures. A vendor changing its tier axes is
 * a NEW sales form: a new profile kind plus a new row here, never a different
 * template on an existing row.
 *
 * The write face (`model-protocols.ts`) enforces VERBATIM equality, not
 * subset: a narrower template is not a modest version of the form, it is a
 * different price structure (the same order billing on a coarser key), and
 * admitting it would re-open exactly the choice this column closes. A spec
 * that declares NO template stays legal — that is the untiered sale, priced
 * on the `(unit, '')` / base row exactly as every stored row predating tiers
 * is, so incumbent registrations are untouched.
 *
 * `ark_video_token_v1` is why the admission set had to go. It admitted
 * `{has_video_input, resolution}`. The 2026-08-16 owner verdict recorded
 * that `resolution` had no measured basis as a token-PRICE axis: #1937's
 * order details showed two per-token rates (`no video input` / `includes
 * video input`), and a higher resolution moving the TOKEN COUNT. That
 * count-moves-with-resolution half remains true (2026-08-30: Seedance 2.0
 * 480p/4s = 40594 tokens, 1080p/4s = 196425 tokens, 4.84×). The RATE
 * half was overturned on 2026-08-30 by per-tier JojoKey
 * `POST /v1/video-cn/price-estimate` replies: each resolution returns its
 * own `base_unit_price_cny_per_million_tokens` and a distinct
 * `price_version_id` (e.g. `video-cn-2.5` 480p
 * `pcprice:20260813:video-cn-2.5:480p:no_media` vs 1080p
 * `pcprice:20260820:video-cn-2.5:1080p:no_media`). Measured list CNY/Mtok
 * (no-media / includes-video): `video-cn-2.0-pro` 480/720p 46/28, 1080p
 * 51/31, 4k 26/16; `video-cn-2.5` 480/720p 70/42, 1080p 77/46 (1080p
 * live 55.44 = 72% promo through 2026-09-17); `video-cn-2.0-mini` 23/14
 * at 480/720p only. Token count AND unit price both move with
 * resolution; the 2026-08-16 inference "so the rate is not tiered" was
 * the wrong half.
 *
 * The single-rate video-input form therefore STAYS `ark_video_token_v1`
 * with template `{has_video_input}`: live SKUs already have
 * `novideoinput` / `includesvideoinput` price rows, and silently widening
 * that template would mismatch every existing key overnight. The
 * resolution-tiered token form is a NEW sales form —
 * `ark_video_token_tiered_v1` — exactly as this column's own rule
 * already stated: "a third tier scheme is a new profile kind plus one
 * row here".
 *
 * `pricedDescriptorFields`: the priced-admissible descriptor fields a
 * quantity term of this profile's stored spec may price off — self-evidence
 * (facts about an ORDER only a specific ingress produces) and, since #1883,
 * terminal-report fields (facts an admission-verified upstream's terminal
 * document states about what it delivered). Which profile may read which is a
 * per-form decision. Observation-grade fields and the upstream's token
 * counters are response-side evidence with their own rules
 * (`DESCRIPTOR_FIELD_META`) and are deliberately outside this table's
 * jurisdiction.
 *
 * Field types are the global unions, so every row is ⊆ the known vocabulary BY
 * TYPE; and the `Record` is exhaustive, so declaring a twelfth profile kind
 * does not compile until its row — and therefore its sales form — is written
 * down.
 */
export interface ProfileMeteringCapability {
  readonly lane: MeteringLane;
  /** The profile's tier axis verbatim, or `null` — this form sells no tiers. */
  readonly fixedVariantKeyTemplate: string | null;
  readonly pricedDescriptorFields: readonly MeteringPricedDescriptorField[];
  /**
   * The second unit term this sales form may sell, or absent — this form
   * sells none, and a stored spec that declares one is refused at write
   * time. Enumerated, not configured: an operator must not be able to
   * invent a surcharge on a profile that does not sell one (#2149).
   */
  readonly surcharge?: {
    readonly unit: UnitUsageUnit;
    readonly pricedDescriptorFields: readonly MeteringPricedDescriptorField[];
  };
}

export const PROFILE_METERING_CAPABILITY: Readonly<
  Record<MeteringProfileKind, ProfileMeteringCapability>
> = {
  anthropic_messages_v2: { lane: 'token', fixedVariantKeyTemplate: null, pricedDescriptorFields: [] },
  openai_responses_v2: { lane: 'token', fixedVariantKeyTemplate: null, pricedDescriptorFields: [] },
  openai_images_v1: { lane: 'token', fixedVariantKeyTemplate: null, pricedDescriptorFields: [] },
  gemini_generate_content_v1: {
    lane: 'token',
    fixedVariantKeyTemplate: null,
    pricedDescriptorFields: [],
  },
  openai_chat_v1: { lane: 'token', fixedVariantKeyTemplate: null, pricedDescriptorFields: [] },
  legacy_4_bucket: { lane: 'token', fixedVariantKeyTemplate: null, pricedDescriptorFields: [] },
  // The Ark-official-compatible task form (#1900/#1937): per-token, in the
  // vendor's own TWO billing units — `no video input` / `includes video
  // input`. `resolution` is deliberately NOT in THIS template. The 2026-08-16
  // owner verdict said the vendor's price list does not tier the token RATE
  // by resolution (a higher resolution produces more tokens, which is already
  // the amount). The count half of that is still true; the rate half was
  // overturned by 2026-08-30 per-tier `price-estimate` measurement — see the
  // column note above. This row is NOT rewritten: live SKUs already price
  // `novideoinput` / `includesvideoinput`, and changing the template would
  // mismatch those keys. The resolution-tiered token form is the next row.
  ark_video_token_v1: {
    lane: 'token',
    fixedVariantKeyTemplate: '{has_video_input}',
    pricedDescriptorFields: [],
  },
  // The resolution-tiered token form of the ark task wire (2026-08-30). Same
  // amount pipeline as `ark_video_token_v1` (upstream terminal
  // `completion_tokens`); different tier matrix:
  // `{has_video_input}-{resolution}`. A new sales form, not a replacement —
  // live SKUs stay on `ark_video_token_v1` until an operator cuts them over
  // and writes the per-resolution price rows. Source of the rate table:
  // JojoKey `POST /v1/video-cn/price-estimate`, per-tier `price_version_id`
  // (see the column-note correction above). 4k is expressible here even
  // while currently unlisted: its list CNY/M is *lower* than 480p's, so a
  // single-rate SKU that opened 4k would overcharge.
  ark_video_token_tiered_v1: {
    lane: 'token',
    fixedVariantKeyTemplate: '{has_video_input}-{resolution}',
    pricedDescriptorFields: [],
  },
  // Sells a video by the SECOND, tiered by resolution (#1838 sub-item D) —
  // the vendor's own per-second price list is resolution-tiered. The count it
  // prices is the seconds the upstream's TERMINAL report states (#1883,
  // ADR-012 §2 as amended); the frozen order is the reconciliation reference.
  duration_seconds_v1: {
    lane: 'unit',
    fixedVariantKeyTemplate: '{resolution}',
    pricedDescriptorFields: ['duration_seconds'],
  },
  // The per-second MATRIX form of the ark task wire (#1883 final sub-item):
  // by the second, priced on a six-cell `has_video_input` x `resolution`
  // matrix, because the upstream's own per-token billing doubles the token
  // count on a video-input order — a per-second resale that ignored the video
  // axis would eat that doubling out of the margin on every video-input
  // order. The amount is the terminal report's seconds x the binding's
  // delivery coefficient, exactly `duration_seconds_v1`'s pipeline; only the
  // tier matrix differs, which under the sales-form model is exactly what a
  // NEW profile kind is for (see the column note above: "a third tier scheme
  // is a new profile kind plus one row here").
  ark_video_seconds_v1: {
    lane: 'unit',
    fixedVariantKeyTemplate: '{has_video_input}-{resolution}',
    pricedDescriptorFields: ['duration_seconds'],
  },
  // MiniMax A on JoJoKey overseas (measured 2026-09-02): per-second on
  // `{resolution}` plus a per-image surcharge. First five reference images
  // are free on the vendor's line items; the stored spec encodes that as
  // `a:1, b:-5, min:0` over the frozen TOTAL count. A different sales form
  // from `duration_seconds_v1` (no surcharge) and `ark_video_seconds_v1`
  // (video-input matrix, no surcharge).
  ark_video_seconds_ref_v1: {
    lane: 'unit',
    fixedVariantKeyTemplate: '{resolution}',
    pricedDescriptorFields: ['duration_seconds'],
    surcharge: {
      unit: 'image',
      pricedDescriptorFields: ['reference_image_count'],
    },
  },
  text_characters_v1: { lane: 'unit', fixedVariantKeyTemplate: null, pricedDescriptorFields: [] },
  flat_call_v1: { lane: 'unit', fixedVariantKeyTemplate: null, pricedDescriptorFields: [] },
  // Per-image, tiered by the canonical size x quality pair (#1668/#1886) —
  // the template is the measured stored-spec shape verbatim, `-` separator
  // included. `images_requested` is admitted because the profile's own
  // built-in spec (`BUILTIN_UNIT_METERING_SPECS`) prices off it, so a stored
  // spec replacing the built-in must be able to keep reading it.
  openai_images_unit_v1: {
    lane: 'unit',
    fixedVariantKeyTemplate: '{size}-{quality}',
    pricedDescriptorFields: ['images_requested'],
  },
  // Grok video as first applicant of the sales-form window (#1949 B / #2138):
  // per-second on `{resolution}` only — no `has_video_input`. That matrix is
  // a different sales form from `ark_video_seconds_v1` and from
  // `duration_seconds_v1` (which stays on `ark_video_v1`); a new tier scheme
  // is a new profile kind (see the column note above).
  //
  // #2149 adds the SECOND unit this form sells: a per-image surcharge,
  // priced off `(image, '')`, because the vendor's measured image add-on
  // is per image, flat across resolution, and independent of duration —
  // none of which is expressible as a per-second rate or a count-bearing
  // tier key. The surcharge is optional on a stored spec (a text-to-video
  // registration that never admits images declares none); declaring one
  // on any other profile is unwritable.
  grok_video_seconds_v1: {
    lane: 'unit',
    fixedVariantKeyTemplate: '{resolution}',
    pricedDescriptorFields: ['duration_seconds'],
    surcharge: {
      unit: 'image',
      pricedDescriptorFields: ['reference_image_count'],
    },
  },
  // Grok still image (#2190): flat empty variant, quantity = delivered
  // `data.length` on the Pool/vendor response. Not a reuse of
  // `openai_images_unit_v1` (ordered `images_requested`, Codex n gated to 1).
  grok_image_unit_v1: {
    lane: 'unit',
    fixedVariantKeyTemplate: null,
    pricedDescriptorFields: ['delivered_image_count'],
  },
};

/**
 * What an unknown profile-kind STRING resolves to: the token lane, and none of
 * the capabilities this table governs.
 *
 * `profile_kind` is a persisted TEXT column and history has already put a
 * retired spelling in one (a dev row still carries the pre-#1839
 * `openai_videos_v1`), so a reader resolving a stored row must not crash on a
 * value outside the closed union. The lane half reproduces exactly what
 * `isUnitMeteredProfileKind`'s false arm always answered for an unknown value;
 * the null template and empty field set are fail-closed on everything this
 * table newly governs.
 */
const UNKNOWN_PROFILE_CAPABILITY: ProfileMeteringCapability = {
  lane: 'token',
  fixedVariantKeyTemplate: null,
  pricedDescriptorFields: [],
};

/** The capability row, tolerant of a stale stored profile-kind string. */
export function meteringCapabilityForProfileKind(profileKind: string): ProfileMeteringCapability {
  const byName: Partial<Record<string, ProfileMeteringCapability>> = PROFILE_METERING_CAPABILITY;
  return byName[profileKind] ?? UNKNOWN_PROFILE_CAPABILITY;
}

/**
 * The lane a metering PROFILE bills on — the fact a stored spec's own `lane`
 * has to agree with (#1937).
 *
 * ONE DERIVATION, because three seats need this answer and a disagreement
 * between them is a mispricing rather than an inconsistency: platform's rating
 * picks which exact-match verdict to publish (`unitRateLookupOf` /
 * `tokenVariantLookupOf` in `rating/event-time-pricing.ts`), the gateway's
 * route gate picks which admission predicate to run, and the ops write face
 * decides which lane's document it may store for a registration. Before this
 * existed the third seat had no rule at all and refused the token lane
 * outright, which made #1937's whole tier feature unreachable through the only
 * entrance an operator has.
 *
 * The membership fact now lives in {@link PROFILE_METERING_CAPABILITY}'s
 * `lane` column and this is a read of it (#1883): the table became the single
 * source when it took on the per-profile field sets, and a second lane rule
 * beside it would be exactly the between-seats disagreement this function
 * exists to prevent. `isUnitMeteredProfileKind` remains the membership test
 * the cost kernel branches on, and `metering-spec.test.ts` pins every row's
 * lane against it. The old caveat — a newly added kind silently landing on the
 * token lane — is now a compile error instead, because the table's `Record` is
 * exhaustive; what stays runtime-tolerant is an unknown STRING off a stored
 * row, which lands on the token lane exactly as it always did (see
 * `meteringCapabilityForProfileKind`).
 */
export function meteringLaneForProfileKind(profile: MeteringProfileKind): MeteringLane {
  return meteringCapabilityForProfileKind(profile).lane;
}

/**
 * The closed set of descriptor fields a spec may read, referenced BY NAME
 * (ADR-012 §2: "collect from several sources, record one fact per source
 * carrying its provenance label, and have specs reference facts by name").
 *
 * Adding a member is the deliberate act that makes a new quantity billable,
 * and it must land together with the gateway code that observes the fact.
 *
 * SPLIT BY ROLE, IN THE TYPE. ADR-012 §2's admissibility matrix is not only a
 * validator rule since it is spelled out here: a self-evidenced fact and an
 * observation are DIFFERENT TYPES, so the two populations are disjoint by
 * construction rather than by a runtime check plus a comment. That buys three
 * things the runtime check alone did not:
 *
 *   - `MeteringQuantityTerm.field` is typed to self-evidence, so a spec that
 *     prices off an observation does not compile — the §2 conflict is
 *     unwritable one layer earlier than the validator;
 *   - `delivery_zeroing_field` is typed to an observation, so self-evidence
 *     cannot reach the seat where it would be the request gating itself;
 *   - `UnitMeteringRequestFacts.fields` (the ROUTE GATE's half) is typed to
 *     self-evidence alone, so the gate is structurally incapable of reading a
 *     settlement-time observation, and the settle's merge of the two halves
 *     cannot collide. That last one was previously asserted by a comment about
 *     object-spread order, and the comment was WRONG about the direction JS
 *     resolves a collision in — which is exactly the kind of claim that should
 *     not have been load-bearing.
 */
export type MeteringSelfDescriptorField = 'images_requested' | 'reference_image_count';

/**
 * TERMINAL-REPORT descriptors (#1883): facts the upstream's terminal document
 * states about what it DELIVERED, admissible as a priced quantity on the unit
 * lane under ADR-012 §2 as amended — "billing locks to delivery". Trust is
 * not free: it is established per supply by the admission action (which
 * includes the refuse-or-silently-substitute probe) and re-checked by #1951's
 * reconciliation, and a supply that misreports is unpinned, never
 * re-interpreted. Distinct from the OBSERVATION grade below in role as well
 * as provenance: an observation may only ZERO a charge; a terminal-report
 * fact may SET one, which is exactly the widening the amendment grants.
 *
 * They are NOT request facts, so `UnitMeteringRequestFacts.fields` cannot
 * carry one and the route gate is structurally incapable of demanding one —
 * the value exists only at settlement, off the terminal document.
 */
export type MeteringTerminalReportDescriptorField =
  | 'duration_seconds'
  | 'delivered_image_count';

/** The fields a priced quantity term may read (ADR-012 §2's priced row). */
export type MeteringPricedDescriptorField =
  | MeteringSelfDescriptorField
  | MeteringTerminalReportDescriptorField;

export type MeteringObservationDescriptorField =
  | 'image_payloads_delivered'
  | 'images_delivered';

export type MeteringDescriptorField =
  | MeteringPricedDescriptorField
  | MeteringObservationDescriptorField;

export const KNOWN_METERING_SELF_DESCRIPTOR_FIELDS: readonly MeteringSelfDescriptorField[] = [
  'images_requested',
  'reference_image_count',
] as const;

export const KNOWN_METERING_TERMINAL_REPORT_DESCRIPTOR_FIELDS:
  readonly MeteringTerminalReportDescriptorField[] = [
    'duration_seconds',
    'delivered_image_count',
  ] as const;

export const KNOWN_METERING_PRICED_DESCRIPTOR_FIELDS:
  readonly MeteringPricedDescriptorField[] = [
    ...KNOWN_METERING_SELF_DESCRIPTOR_FIELDS,
    ...KNOWN_METERING_TERMINAL_REPORT_DESCRIPTOR_FIELDS,
  ] as const;

export const KNOWN_METERING_OBSERVATION_DESCRIPTOR_FIELDS:
  readonly MeteringObservationDescriptorField[] = [
    'image_payloads_delivered',
    'images_delivered',
  ] as const;

export const KNOWN_METERING_DESCRIPTOR_FIELDS: readonly MeteringDescriptorField[] = [
  ...KNOWN_METERING_PRICED_DESCRIPTOR_FIELDS,
  ...KNOWN_METERING_OBSERVATION_DESCRIPTOR_FIELDS,
] as const;

/** The self-evidenced field of that name, or `undefined`. Type-narrowing. */
function selfDescriptorFieldNamed(
  name: MeteringDescriptorField,
): MeteringSelfDescriptorField | undefined {
  return KNOWN_METERING_SELF_DESCRIPTOR_FIELDS.find((candidate) => candidate === name);
}

/** The terminal-report field of that name, or `undefined`. Type-narrowing. */
function terminalReportDescriptorFieldNamed(
  name: MeteringDescriptorField,
): MeteringTerminalReportDescriptorField | undefined {
  return KNOWN_METERING_TERMINAL_REPORT_DESCRIPTOR_FIELDS.find((candidate) => candidate === name);
}

/** The priced-admissible field of that name, or `undefined`. Type-narrowing. */
function pricedDescriptorFieldNamed(
  name: MeteringDescriptorField,
): MeteringPricedDescriptorField | undefined {
  return KNOWN_METERING_PRICED_DESCRIPTOR_FIELDS.find((candidate) => candidate === name);
}

/** The observation of that name, or `undefined`. Type-narrowing. */
function observationDescriptorFieldNamed(
  name: MeteringDescriptorField,
): MeteringObservationDescriptorField | undefined {
  return KNOWN_METERING_OBSERVATION_DESCRIPTOR_FIELDS.find((candidate) => candidate === name);
}

/**
 * Provenance and observability of one descriptor field.
 *
 * `protocols` is enforced: a fact that only exists on one wire may only be
 * read by a spec that has declared it reads that wire, so a spec cannot
 * silently evaluate to zero-of-a-fact-that-cannot-exist on another protocol.
 *
 * `grade` is ADR-012 §2's evidence class, and since round two it is ENFORCED
 * by the validator — §2 asks for exactly that ("a role x grade admissibility
 * matrix is enforced by the validator at spec-write time, not by the reader at
 * billing time"). The matrix has two rows and they are the whole of it:
 *
 *   PRICED role (a `quantity` term's `field`, and every `variant_key`
 *   placeholder): SELF-evidence only. §2: "This is the only evidence that may
 *   set a priced quantity or a priced tier."
 *
 *   ZEROING role (`delivery_zeroing_field`): an OBSERVATION only —
 *   first-party or third-party, never self. §2's directional exception lets an
 *   observation CANCEL a charge and never set, raise or restore one, and a
 *   self value in that seat would be the request gating itself.
 *
 * That enforcement is what closes §2's recorded known conflict: an
 * `images_delivered`-priced unit-lane profile is now unwritable, not merely
 * discouraged.
 *
 * Absence is read differently per grade, and the difference is the point:
 *
 *   A missing OBSERVATION is NOT zero either — it is UNOBSERVABLE, and a spec
 *   that declared a gate on it cannot be evaluated truthfully, so the evaluator
 *   authors no block and the event quarantines loudly as `invalid_usage`.
 *   The soft-refusal case is a MEASURED zero (`0`), not an absent one: the
 *   gateway counted the payloads in a body it held and found none, and that
 *   settles as the whitelisted `unit_not_delivered` zero. Every producer of a
 *   descriptor is required to distinguish the two — a wire that can carry the
 *   fact passes `0` when it did not carry it, and only a transport on which
 *   the fact cannot be observed at all passes nothing. Collapsing them is a
 *   100%-of-revenue silent zero on the first SKU whose gate rides a transport
 *   that cannot be measured.
 *
 *   A missing SELF value is NOT zero. Self-evidence is a canonical value the
 *   gateway either produced or did not; reading "absent" as zero would bill a
 *   delivered request nothing on any ingress that does not carry the fact.
 *   The spec is therefore UNEVALUABLE, which the route gate turns into 400
 *   `MODEL_BILLING_UNAVAILABLE` before any upstream spend.
 */
interface MeteringDescriptorFieldMeta {
  readonly grade: 'self' | 'terminal_report' | 'first_party' | 'third_party';
  /** Protocols on which the fact is observable; `null` means protocol-agnostic. */
  readonly protocols: readonly ModelProtocol[] | null;
}

const DESCRIPTOR_FIELD_META: Readonly<
  Record<MeteringDescriptorField, MeteringDescriptorFieldMeta>
> = {
  // SELF: the canonical image count the gateway itself produced from the
  // request (native Images admits official n 1–10; Codex / Responses bypass
  // still admits one). Protocol-agnostic because it is a fact about the
  // ORDER, not about any upstream's wire — and it is absent, not zero, on an
  // ingress that carries no ordered image count.
  images_requested: { grade: 'self', protocols: null },
  // SELF (#2149): the count of reference images the gateway itself admitted
  // from the Grok video order (`reference_images`). The delivery side
  // states nothing about reference images and structurally cannot — the
  // terminal document reports duration and resolution, never the images
  // that were consumed — so the request-side fact keeps the seat, the
  // same sentence #1883 uses for the price TIER. This is not a new
  // evidence grade and not an ADR-012 §2 amendment: `images_requested`
  // has been a request-side self-evidenced priced quantity since #1668,
  // and `MeteringPricedDescriptorField` already includes the self
  // population. The obvious reading of #1883 ("quantities are
  // terminal-report grade") is the narrower one and is about
  // `duration_seconds` specifically.
  //
  // `['grok_video_v1', 'ark_video_v1']`: Grok video (#2149) and MiniMax A
  // on the ark task wire (JoJoKey overseas, measured 2026-09-02). A spec
  // pricing off it must declare it reads one of those wires.
  reference_image_count: { grade: 'self', protocols: ['grok_video_v1', 'ark_video_v1'] },
  // TERMINAL-REPORT (#1883, reversing the original grade): the seconds the
  // upstream's terminal `Success` document states (`usage.duration`, read by
  // `arkTaskTerminalDurationSeconds` at settlement and nowhere else). This
  // field used to be SELF grade — the frozen order's canonical seconds — and
  // the module used to argue at length that the terminal figure was the same
  // number echoed back and therefore inadmissible. The owner's verdict keeps
  // the measurement and reverses the conclusion: billing locks to what was
  // DELIVERED, the echo equals the delivery on a supply whose admission
  // action verified it refuses out-of-domain orders rather than silently
  // substituting, and a supply that misreports is unpinned rather than
  // re-interpreted (#1951 reconciles the residue). The frozen order survives
  // as the reconciliation reference, not as a pricing input — settlement
  // NEVER falls back to it: a terminal document with no usable figure
  // quarantines the task at zero charge.
  //
  // `['ark_video_v1', 'grok_video_v1']`: the fact exists on the ark task
  // wire's terminal document and on the Grok video terminal document
  // (`duration_seconds`, pool #4763). A spec pricing off it must declare it
  // reads one of those wires. Do NOT add chat protocols: a duration_seconds
  // term on openai would silently evaluate to zero-of-a-fact-that-cannot-exist.
  duration_seconds: { grade: 'terminal_report', protocols: ['ark_video_v1', 'grok_video_v1'] },
  // TERMINAL-REPORT (#2190): count of entries in the vendor `data` array
  // on a 2xx Grok image response. URL and b64 both count — this is
  // `data.length`, not base64 payload bytes. Priced quantity for
  // `grok_image_unit_v1`; `images_requested` would overcharge when n>1
  // and the vendor delivered fewer. `['grok_image_v1']` only.
  delivered_image_count: { grade: 'terminal_report', protocols: ['grok_image_v1'] },
  // FIRST-party: the gateway counted the image payloads in the response body
  // it holds. ADR-012 §2 names exactly this ("counting the image payloads the
  // gateway can see in the response body it already holds") as the first-party
  // example, and §2's repair paragraph requires it to be ATTEMPTED before the
  // third-party fallback. Only the OpenAI wires carry a payload shape the
  // gateway can count, so a spec reading it must declare `protocols:
  // ['openai']`.
  image_payloads_delivered: { grade: 'first_party', protocols: ['openai'] },
  // THIRD-party, not first-party: the upstream's response body decides whether
  // a `tool_usage.image_gen` block exists at all, and the gateway's `1` is a
  // constant it substitutes for that assertion, not a measurement it took of
  // the delivered artifact. Retained as a LEGAL zeroing field — §2's
  // directional exception admits it there — for a future wire on which
  // first-party payload counting is impossible. It is no longer readable as a
  // quantity, which is the §2 conflict being closed.
  images_delivered: { grade: 'third_party', protocols: ['openai'] },
};

/**
 * How this request's canonical tier reached the upstream — the EGRESS SHAPE.
 *
 * This is the `protocols` mechanism of `DESCRIPTOR_FIELD_META` applied to the
 * priced-TIER row of ADR-012 §2's matrix: a fact that is only self-evidence on
 * some egress shapes may only be priced on a request that took one of them.
 *
 * - `canonical_overwrite`: the gateway performed the whole of §2's
 *   parse → clamp → OVERWRITE — it wrote its own canonical value into the
 *   outbound body, and the upstream this request can reach is armed as one
 *   that honours it. What was billed is what was ordered is what was sent.
 * - `not_forwarded`: anything else, and it is the DEFAULT direction. The
 *   Responses bypass is here (it drops `quality` and turns `size` into an
 *   aspect-ratio sentence), so is every ingress that carries no tier at all,
 *   and so is a retry chain that could still land on either.
 *
 * The distinction is not knowable at spec-write time — it is a property of the
 * ROUTE, resolved per request — which is why it is a fact on
 * `UnitMeteringRequestFacts` checked by the route gate, and not a validator
 * rule. A validator that tried to answer it would be answering for every
 * upstream a SKU may ever be routed to.
 */
export type MeteringVariantEgress = 'canonical_overwrite' | 'not_forwarded';

/**
 * The canonical request values a `variant_key` template placeholder may name.
 *
 * ── OPEN SINCE #1886 ROUND TWO, AND ONLY ON ONE EGRESS SHAPE ───────────────
 *
 * This list was EMPTY, and it was the gate on tiered pricing: no placeholder
 * validated, no `variant_key` but `''` could be produced, and the whole tiered
 * lane was fail-closed end to end. What held it shut was ADR-012 §4's
 * precondition 2 — the tier must actually be FORWARDED and HONOURED — which
 * was false on both egress shapes `/v1/images/*` had:
 *
 *   - the Responses bypass never forwarded `quality` ("fixed at medium on this
 *     path — passing it through would be theatre") and did not honour `size`
 *     in any form (it becomes an aspect-RATIO sentence inside the prompt),
 *     and it recomputes both echoes from what the upstream actually DID; and
 *   - the native passthrough (#1886 round one) forwarded both verbatim,
 *     `quality: 'auto'` included — so a request that left the tier open still
 *     had its tier chosen by the upstream.
 *
 * ROUND TWO CLOSES THE SECOND ONE ONLY, and the closure is the OVERWRITE step:
 * `canonicalImagesRequest` in the gateway's `canonical-request.ts` resolves
 * `auto` and absence into an explicit tier and writes that tier into the
 * outbound body, so on the native passthrough the value billed, the value
 * ordered and the value sent are one value produced once.
 *
 * IT DOES NOT CLOSE THE FIRST ONE, and nothing here pretends it does. A
 * request that reaches the Responses bypass — or a retry chain that could
 * still fall back to it — carries `variantEgress: 'not_forwarded'`, every
 * placeholder is then inadmissible by `VARIANT_FIELD_META` below, and the
 * route gate refuses with `variant_not_forwarded` (400, before any spend)
 * rather than pricing a tier that route discards. THAT is why this list can be
 * populated while the bypass still exists: admissibility is per REQUEST, not
 * per repository state.
 *
 * ADR-012 §4 precondition 1 — the enumerated `size` x `quality` surface — is
 * closed as DATA rather than as a second constant here: a spec that declares a
 * template MUST declare `quantity_by_variant` (validator rule
 * `variant_template_without_coefficients`), the lookup is EXACT MATCH, and the
 * route gate refuses an uncovered tier with `variant_uncovered` before any
 * spend. The enumeration is therefore the spec's own key set — about twelve
 * bounded rows, per §4 — and a request outside it is refused, which is the
 * "clamped or refused" §4 asks for. A global enum here would have been a
 * second, weaker copy of a rule the exact-match table already enforces.
 *
 * ── `resolution` (#1838 sub-item D): A THIRD MEMBER, ON ITS OWN EVIDENCE ───
 *
 * Everything above is an argument about `/v1/images/*` and its two egress
 * shapes. **None of it reaches `resolution`, and none of it is weakened by
 * admitting it.** The two claims have to be kept apart, because the tempting
 * mistake is to read "the tier gate is open" as one fact about this file:
 *
 *  - The Responses bypass — the route that drops `quality` and turns `size`
 *    into an aspect-ratio sentence — is a property of the IMAGES ingress. The
 *    asynchronous task ingress has one egress shape and no bypass: it dials
 *    `gate.plan.canonical.parameters` and nothing else, so the tier that was
 *    parsed and clamped is the tier that was sent, on every request it serves.
 *  - Admissibility stays PER FIELD and PER REQUEST regardless. `size` and
 *    `quality` keep their own `VARIANT_FIELD_META` rows, listing
 *    `canonical_overwrite` and nothing else, and `resolveVariantKey` checks
 *    the row of the field a placeholder NAMES. So a `{size}-{quality}`
 *    template on a `not_forwarded` request is refused exactly as before —
 *    admitting a third field cannot loosen a rule that is keyed on the first
 *    two. `metering-spec.test.ts` pins that cell rather than trusting this
 *    paragraph.
 *
 * `resolution` earns its own row on ADR-012 §4's two preconditions, met by the
 * task lane's own data rather than by inheriting the images argument:
 *
 *  1. **Enumerated.** It is already ADR-013 §3's `tier_class_parameter`, which
 *     `validateArkTaskAdmissionSurface` requires to be an `enum` rule carrying
 *     a `default` — a closed, operator-declared value list, cross-checked
 *     against the allowance map's key set in both directions. The metering
 *     spec's own `quantity_by_variant` exact-match rule sits on top of it.
 *  2. **Forwarded and honoured.** `tasks/submit.ts` sends the canonical object
 *     verbatim as the dial's `parameters`, so the OVERWRITE step ADR-012 §2
 *     asks for is performed in full and the tenant's own `parameters` never
 *     reach the upstream.
 *
 * `VARIANT_FIELD_META` still lists `canonical_overwrite` alone for it. That is
 * not ceremony: the egress shape is a property of the ROUTE, and a future
 * ingress that carried a `resolution` it did not honour must refuse rather
 * than inherit today's single-shape convenience.
 *
 * ── `has_video_input` (#1937): A FOURTH MEMBER, ON A NEW EVIDENCE SOURCE ───
 *
 * The three members above are all PARAMETERS: the gateway parses a declared
 * key out of the request's `parameters` object, clamps it, and writes it back.
 * `has_video_input` is the first member whose value is not a parameter at all
 * — it is a fact about `input.content`, namely whether the order carries a
 * `video_url` member. That is a genuinely different source, so it gets its own
 * argument on ADR-012 §4's two preconditions rather than inheriting
 * `resolution`'s:
 *
 *  1. **Enumerated.** The domain is two values and they are CONSTANTS, not
 *     operator text: {@link VIDEO_INPUT_VARIANT_VALUES}. There is no third
 *     spelling to price, so the "clamped or refused" §4 asks for is satisfied
 *     by the field being a boolean rendered into a closed pair.
 *  2. **Forwarded and honoured.** `submit.ts` dispatches
 *     `gate.plan.canonical.content` verbatim — the same array the gate read
 *     the fact out of — and #1937 established that byte-for-byte identity
 *     white-box, by recomputing `gate.ts`'s request fingerprint from the
 *     submitted content and matching it against the stored
 *     `generation_tasks.request_fingerprint` for a `video_url` order, an `image_url`
 *     order and a text-only positive control. So the presence the gate saw is
 *     the presence the upstream is billed on.
 *
 * WHAT DECIDES IT IS `video_url` AND ONLY `video_url`, which is measured
 * rather than reasoned: #1937's order detail shows an `image_url` order
 * landing on the vendor's `no video input` billing unit at the SAME token
 * count as a text-only order (40594 at 480P/4s), while a `video_url` order
 * lands on `includes video input` at 80770. Widening the predicate to "any
 * non-text member" would price every image order on the wrong tier.
 *
 * `VARIANT_FIELD_META` lists `canonical_overwrite` alone for it, like the
 * other three: an ingress that read the content but dispatched something else
 * would be `not_forwarded` and refused, which is the property that has to
 * survive a second task egress shape being added.
 */
export type MeteringVariantField = 'size' | 'quality' | 'resolution' | 'has_video_input';

export const KNOWN_VARIANT_KEY_FIELDS: readonly MeteringVariantField[] = [
  'size',
  'quality',
  'resolution',
  'has_video_input',
] as const;

/**
 * The closed domain of {@link MeteringVariantField} `has_video_input`, spelled
 * once (#1937).
 *
 * The two strings are the vendor's own billing-unit words — `no video input`
 * and `includes video input`, transcribed from the measured order detail —
 * folded into the `variant_key` alphabet by removing the spaces. A
 * `variant_key` value may contain only `[a-z0-9]`: `-` and `_` are template
 * SEPARATORS, and the injectivity premise on {@link VARIANT_KEY_TEMPLATE}
 * holds only while no admitted value can contain one. So the vendor's literal
 * cannot ride through unchanged, and the fold is stated here rather than
 * performed ad hoc at the producer, where a second producer would fold it
 * differently and price the same order on two keys.
 *
 * `includes`, not `has`: the vendor's cheap tier is spelled `includes video
 * input`, and #1937 records that mis-transcribing it as `video input` was one
 * of the things the measurement corrected.
 */
export const VIDEO_INPUT_VARIANT_VALUES = {
  absent: 'novideoinput',
  present: 'includesvideoinput',
} as const;

/**
 * The egress shapes on which each variant field is self-evidence.
 *
 * Both fields admit `canonical_overwrite` and nothing else, and the list is
 * per-FIELD rather than a single global rule so that a future field whose
 * value survives a codec (an ingress-side parameter the bypass does forward)
 * can be admitted on more shapes without loosening these two.
 */
interface MeteringVariantFieldMeta {
  readonly egress: readonly MeteringVariantEgress[];
}

const VARIANT_FIELD_META: Readonly<Record<MeteringVariantField, MeteringVariantFieldMeta>> = {
  size: { egress: ['canonical_overwrite'] },
  quality: { egress: ['canonical_overwrite'] },
  // The task lane performs the whole overwrite and has no second egress shape,
  // so every request it serves reports `canonical_overwrite` — but the rule is
  // still written per field and read per request. An ingress that parsed a
  // `resolution` it did not forward would be `not_forwarded` and refused here,
  // which is the property that survives the next ingress being added.
  resolution: { egress: ['canonical_overwrite'] },
  // #1937. Same single shape, and for a reason that is NOT the same as
  // `resolution`'s: this value is read out of `input.content`, and the task
  // ingress dispatches `gate.plan.canonical.content` — the very array it was
  // read from. An ingress that read the content and then rebuilt a body from
  // something else would have to report `not_forwarded` and be refused here.
  has_video_input: { egress: ['canonical_overwrite'] },
};

function variantFieldNamed(name: string): MeteringVariantField | undefined {
  return KNOWN_VARIANT_KEY_FIELDS.find((candidate) => candidate === name);
}

/**
 * The distinct variant fields a template's placeholders name, in template
 * order; `undefined` — the no-variant case — names nothing.
 *
 * For a template that passed `validateVariantKeyTemplate` this is total. An
 * unknown placeholder name is skipped rather than reported, deliberately: the
 * callers read a VALIDATED template — a stored spec's, or a profile's
 * `fixedVariantKeyTemplate` (`PROFILE_METERING_CAPABILITY`) — to derive its
 * field structure for display and key parsing, and the unknown-name refusal
 * belongs to the validator, which has already run.
 */
export function variantFieldsInTemplate(
  template: string | undefined,
): readonly MeteringVariantField[] {
  if (template === undefined) return [];
  const fields: MeteringVariantField[] = [];
  for (const match of template.matchAll(VARIANT_KEY_PLACEHOLDER)) {
    const name = match[1];
    const field = name === undefined ? undefined : variantFieldNamed(name);
    if (field !== undefined && !fields.includes(field)) fields.push(field);
  }
  return fields;
}

/**
 * One affine term of ADR-012 §3's algebra: `clamp(a x field + b, min, max)`.
 *
 * `field` is optional and its absence is not a shorthand: it means the term
 * reads NO descriptor, so the quantity is the constant `b`. The validator
 * requires `a === 0` in that case — a non-zero coefficient with nothing to
 * multiply is a spec whose author believed a fact was being read.
 *
 * `min`/`max` are optional and unbounded when absent. They are NOT defaulted
 * to `[0, Infinity]`: a default clamp would silently rewrite an out-of-range
 * observation into a plausible number, and the unit branch of
 * `computeCostBreakdownUsdMicros` already quarantines a quantity that is not a
 * non-negative safe integer as `cost_overflow` rather than billing it.
 *
 * ── ALL FOUR NUMBERS ARE SAFE INTEGERS, AND THE VALIDATOR ENFORCES IT ─────
 *
 * `a`, `b`, `min` and `max` must each be a safe integer. Finiteness — which is
 * all this used to ask for — is not enough, and the gap it left is a SILENT
 * TOTAL LOSS rather than a rounding annoyance:
 *
 *   `computeUnitCostBreakdown` (`metering.ts`) requires the evaluated quantity
 *   to satisfy `Number.isSafeInteger`, and a quantity that does not is not an
 *   error, a refusal or an alert — it is `cost_overflow`, which rates as ZERO.
 *   The request is served, the artifact is delivered, the upstream is paid, and
 *   the event lands in the ordinary anomaly bucket at $0.
 *
 * So `{ field: 'duration_seconds', a: 1.5, b: 0 }` — a perfectly plausible
 * "bill 1.5 units per second" — makes that SKU free for every odd duration and
 * priced for every even one, with nothing anywhere saying so. Integrality of
 * all four numbers is the only static property that makes the result an
 * integer for every integer descriptor value the algebra can be handed, so it
 * is checked at spec-write time where it costs a 400 to a reader who is looking
 * at the document.
 *
 * A fractional RATE is still expressible and is where it belongs: the price
 * table's `(unit, variant_key)` row carries micros per unit, so "half a cent
 * per second" is a price, not a quantity. The quantity is a count of things.
 */
export interface MeteringQuantityTerm {
  /**
   * PRICED-admissible evidence only, in the type (ADR-012 §2's priced row as
   * amended by #1883): self-evidence, or a terminal-report field from an
   * admission-verified supply. An OBSERVATION here is still unwritable — an
   * observation may only zero a charge — and it fails to compile as well as
   * failing validation.
   */
  readonly field?: MeteringPricedDescriptorField;
  readonly a: number;
  readonly b: number;
  readonly min?: number;
  readonly max?: number;
}

/**
 * How the `delivered` flag of the emitted block is decided.
 *
 * - `forwarding_proof`: delivery is settlement's four-axis forwarding proof
 *   verbatim (billable route, 2xx, no `admission` block, stream terminated).
 *   Correct when the CALL is the deliverable, as for `flat_call_v1`.
 * - `positive_quantity`: the forwarding proof must ALSO have produced a
 *   positive quantity. Correct when an artifact is the deliverable, as for
 *   `openai_images_unit_v1`, where a soft refusal (a genuine 200 carrying
 *   prose and no image) passes the four axes and still delivered nothing.
 *
 * Since the quantity is already zeroed when the forwarding proof fails, the
 * second gate is exactly `quantity > 0`.
 */
export type UnitDeliveryGate = 'forwarding_proof' | 'positive_quantity';

/**
 * A unit-lane metering spec: the whole of what the gateway needs to turn a
 * settled request into a priceable `unit_usage` block.
 *
 * Stored (from #1878 round two) as JSON in
 * `model_metering_profiles.billable_dimensions_json` and shipped to the
 * gateway on the existing `pub_*` metering-profile projection, so
 * `spec_version` is a persisted-shape version and moves only when the stored
 * shape changes incompatibly.
 */
export interface UnitLaneMeteringSpec {
  readonly spec_version: 1;
  readonly lane: 'unit';
  /** The billed unit; must have a matching `(unit, variant_key)` price row. */
  readonly unit: UnitUsageUnit;
  /** The term for the no-variant case, i.e. when no template is declared. */
  readonly quantity: MeteringQuantityTerm;
  /**
   * Per-variant coefficients, keyed by the resolved `variant_key` (ADR-012
   * §3: "coefficients per variant"). MUTUALLY REQUIRED with
   * `variant_key_template` — neither key may appear without the other — and
   * keys must be non-empty because the no-variant case is `quantity` itself.
   *
   * The "template requires a table" half is ADR-012 §4 precondition 1: this
   * key set is the ENUMERATED parameter surface. Without it a template would
   * price every tier the canonicalizer can spell on the base term, which is
   * the unbounded `size` x `quality` table §4 refuses.
   *
   * NOT a fallback table. When this is declared the lookup is EXACT MATCH: a
   * resolved variant with no entry here refuses to produce a block rather than
   * falling back to `quantity`. That mirrors §3's price rule deliberately —
   * a declared-but-uncovered tier billed on the default coefficients is the
   * same silent undercharge as one billed on the `(unit, '')` default price
   * row, and §3's "declaring a template forbids the default" reads on the
   * whole declaration, not on the price half of it.
   *
   * That refusal is DEPTH, not the main line. Refusing at evaluation means the
   * request was already served: gate passed → upstream billed us → artifact
   * delivered → no block → quarantine → $0, i.e. loudly free, while ADR-012
   * §3's *Cost accepted* names the intended shape as a REFUSED request.
   * `variant_key` comes from parse → clamp → overwrite at ingress, before
   * routing, so the route gate can and must check coverage — coefficient entry
   * AND price row — before admitting the request. See the coverage SEAM on
   * `builtinUnitMeteringSpec`.
   */
  readonly quantity_by_variant?: Readonly<Record<string, MeteringQuantityTerm>>;
  readonly delivery_gate: UnitDeliveryGate;
  /**
   * An OBSERVATION that may only ZERO the quantity — ADR-012 §2's directional
   * exception, and the seat the unit lane gives evidence it does not trust.
   *
   * When declared, a non-positive value on that field zeroes the quantity
   * exactly as a failed forwarding proof does; it can never set, raise or
   * restore one. The zeroing happens BEFORE the term is read, so the emitted
   * block is the same `quantity: 0` an undelivered settle emits — a
   * `positive_quantity` gate then reports `delivered: false` and the platform
   * kernel takes its whitelisted `unit_not_delivered` zero.
   *
   * Grade-restricted to first- or third-party (see `DESCRIPTOR_FIELD_META`):
   * self-evidence in this seat would be the request gating itself, which
   * proves nothing about delivery. §2 requires the FIRST-party option to be
   * attempted before the third-party one; the validator cannot see which is
   * available on a given wire, so that ordering is enforced by review of the
   * spec, and the built-in below records the evidence for its own choice.
   */
  readonly delivery_zeroing_field?: MeteringObservationDescriptorField;
  /**
   * Ingress protocols on which this spec can be evaluated truthfully. Absent
   * means protocol-agnostic. On a protocol outside the set the evaluator
   * authors NO block, so the event quarantines platform-side as
   * `invalid_usage` — a loud zero, never a silent free ride.
   */
  readonly protocols?: readonly ModelProtocol[];
  /**
   * The `variant_key` template, e.g. `{size}-{quality}`. Declaring one is a
   * money rule (ADR-012 §3): it forbids the `(unit, '')` default row as a
   * pricing fallback, so the price lookup for such a spec is exact-match only.
   *
   * Placeholders name canonical request values (`KNOWN_VARIANT_KEY_FIELDS`),
   * never tenant body fields, and a placeholder the gateway could not
   * canonicalize makes the spec unevaluable rather than resolving to a
   * partial key — see `resolveVariantKey`.
   */
  readonly variant_key_template?: string;
  /**
   * An optional second unit term with its OWN unit and quantity, priced
   * independently of the primary term's `variant_key` (#2149).
   *
   * There is NO `variant_key_template`: the measured Grok image surcharge
   * is per image, flat across resolution, and independent of duration, so
   * inventing a variant (the resolution, say) would encode a dimension
   * the measurement denies. It always resolves `variant_key: ''` and is
   * priced off the `(surcharge.unit, '')` row.
   *
   * Which profiles may declare one is enumerated on
   * {@link PROFILE_METERING_CAPABILITY}'s `surcharge` column — an operator
   * must not be able to invent a surcharge on a profile that does not
   * sell one. Structural legality is this validator; the profile
   * permission is the write face.
   */
  readonly surcharge?: {
    readonly unit: UnitUsageUnit;
    readonly quantity: MeteringQuantityTerm;
  };
}

/**
 * A TOKEN-lane metering spec: the price-tier axis, and deliberately nothing
 * else (#1937, ADR-012 §3/§4).
 *
 * ── WHAT IT CANNOT SAY, WHICH IS MOST OF WHAT A UNIT SPEC SAYS ────────────
 *
 * No `unit`, no `quantity`, no `quantity_by_variant`, no `delivery_gate`, no
 * `delivery_zeroing_field`. The validator refuses every one of those keys on a
 * `lane: 'token'` document, so this shape cannot become the lane bridge
 * ADR-012 §4 forbids: the amount stays the upstream's `usage` read by a
 * per-protocol extractor, and nothing an operator can write here reaches it.
 *
 * ── WHAT IT DOES SAY, AND WHY IT IS SELF-EVIDENCE ─────────────────────────
 *
 * `variant_key_template` names canonical request values from
 * `KNOWN_VARIANT_KEY_FIELDS`, exactly as a unit spec's does, and resolves
 * through the same `resolveVariantKey`. ADR-012 §2's priced-TIER row admits
 * self-evidence only, and the token lane changes nothing about that: the
 * upstream's response body is third-party evidence and remains unable to name
 * a variant field, because the vocabulary gives it no name to be read by.
 *
 * ── DECLARING ONE IS A MONEY RULE ─────────────────────────────────────────
 *
 * The same rule §3 states for the unit lane applies here: a declared template
 * forbids the default `variant_key: ''` price row as a FALLBACK, so an
 * unpriced tier refuses rather than billing at whichever tier happens to be
 * the base row. On this lane the stakes are the measured 46/28 spread, and the
 * direction of the error is not fixed — it flips with the traffic mix, which
 * is why "close enough on average" is not available.
 *
 * `variant_key_template` is REQUIRED here, unlike on the unit lane where it is
 * optional. A token-lane spec exists only to declare the tier axis; one
 * without a template would be a document that says nothing, and the state it
 * would express — "no tiers" — is already spelled by having no stored spec at
 * all.
 */
export interface TokenLaneMeteringSpec {
  readonly spec_version: 1;
  readonly lane: 'token';
  /**
   * Ingress protocols on which this tier axis can be resolved truthfully.
   * Absent means protocol-agnostic. Same meaning as the unit lane's.
   */
  readonly protocols?: readonly ModelProtocol[];
  /** e.g. `{has_video_input}-{resolution}`. Required: see the note above. */
  readonly variant_key_template: string;
}

export type MeteringSpec = UnitLaneMeteringSpec | TokenLaneMeteringSpec;

/** Why a candidate spec is not a legal spec. */
export type MeteringSpecViolation =
  | 'not_an_object'
  | 'unknown_spec_version'
  | 'unknown_lane'
  | 'unknown_unit'
  | 'unknown_delivery_gate'
  | 'unknown_key'
  | 'invalid_quantity_term'
  /**
   * A coefficient or a bound was finite but not a safe INTEGER — see the
   * integrality note on {@link MeteringQuantityTerm}. It is separate from
   * `invalid_quantity_term` because it is a different repair: the document is
   * well-formed and the number is the thing that is wrong.
   */
  | 'non_integer_quantity_term'
  | 'unknown_descriptor_field'
  | 'coefficient_without_descriptor'
  | 'invalid_bounds'
  | 'invalid_protocols'
  | 'descriptor_protocol_undeclared'
  /** A priced quantity read something other than self-evidence (ADR-012 §2). */
  | 'priced_descriptor_not_self_evidence'
  /** `delivery_zeroing_field` named self-evidence, or an unknown field. */
  | 'invalid_delivery_zeroing_field'
  | 'invalid_variant_key_template'
  | 'unknown_variant_key_field'
  | 'variant_coefficients_without_template'
  /**
   * A template was declared with no `quantity_by_variant` table — ADR-012 §4
   * precondition 1, the CLOSED parameter surface, as a validator rule. Without
   * the table every resolvable key is served on the base term, so the surface
   * is as open as the canonicalizer's grammar and the price table it implies
   * is unbounded. With it, the key set IS the enumeration and an uncovered
   * tier is refused at the route gate before any spend.
   */
  | 'variant_template_without_coefficients'
  | 'invalid_variant_coefficients'
  /**
   * A `lane: 'token'` document carried a UNIT-lane key (#1937). Reported as
   * its own code rather than as `unknown_key` because the repair is different
   * and the mistake is a real one an operator will make: the keys ARE legal
   * spec keys, just not on this lane, and folding them into `unknown_key`
   * would send a reader hunting for a typo that is not there.
   */
  | 'lane_key_not_admitted'
  /**
   * A token-lane document declared no `variant_key_template` (#1937). The only
   * thing such a spec can say is which tiers exist; one that says nothing is
   * indistinguishable from having no stored spec, and shipping it would put a
   * document in the catalog that changes no behaviour while looking like it
   * does.
   */
  | 'token_template_required'
  /**
   * The template validates structurally but can produce a `variant_key` the
   * PRICE table's own rule refuses (#1937) — an `_`, typically. See
   * `templateProducesLegalVariantKeys`: the two write faces used to disagree
   * silently, and this is that disagreement moved to spec-write time where it
   * costs a 400 to the operator who can fix it.
   */
  | 'variant_key_template_unpriceable'
  /**
   * A `surcharge` object was declared illegally (#2149): not a plain
   * object, unknown keys, or missing `unit` / `quantity`. Distinct from
   * `unknown_key` (the key name is legal) and from `unknown_unit` /
   * `invalid_quantity_term` (those still fire on the inner fields).
   */
  | 'invalid_surcharge';

export type MeteringSpecValidation =
  | { readonly ok: true; readonly spec: MeteringSpec }
  | { readonly ok: false; readonly violations: readonly MeteringSpecViolation[] };

const SPEC_KEYS: ReadonlySet<string> = new Set([
  'spec_version',
  'lane',
  'unit',
  'quantity',
  'quantity_by_variant',
  'delivery_gate',
  'delivery_zeroing_field',
  'protocols',
  'variant_key_template',
  'surcharge',
]);

/**
 * The keys a `lane: 'token'` document may carry (#1937). Everything else in
 * `SPEC_KEYS` is unit-lane vocabulary and is refused with
 * `lane_key_not_admitted` — which is what keeps a token spec from acquiring a
 * quantity, and therefore from becoming the lane bridge ADR-012 §4 forbids.
 */
const TOKEN_LANE_SPEC_KEYS: ReadonlySet<string> = new Set([
  'spec_version',
  'lane',
  'protocols',
  'variant_key_template',
]);

const TERM_KEYS: ReadonlySet<string> = new Set(['field', 'a', 'b', 'min', 'max']);

const UNIT_DELIVERY_GATES: readonly UnitDeliveryGate[] = [
  'forwarding_proof',
  'positive_quantity',
] as const;

/**
 * `{name}` placeholders over `[a-z0-9_]`, joined by literal `[a-z0-9_-]` — and
 * ADJACENT PLACEHOLDERS MUST BE SEPARATED BY AT LEAST ONE `[_-]`.
 *
 * That last clause is the injectivity premise, and it used to be missing. The
 * previous form allowed an EMPTY literal between placeholders, so
 * `{quality}{size}` validated and then collided: `quality='low1'` with
 * `size='24x24'` and `quality='low'` with `size='124x24'` both resolve to
 * `low124x24`. One key is one price row, so two distinct tiers billing at one
 * rate is a mispricing that no downstream check can see — the key is already
 * ambiguous by the time anything reads it.
 *
 * Injectivity needs BOTH halves and neither is sufficient alone:
 *
 *   1. a separator that must be PRESENT between adjacent placeholders — this
 *      regex; and
 *   2. a separator that CANNOT OCCUR inside any admitted value — the
 *      `[a-z0-9]`-only rule on canonical values in the gateway's
 *      `canonical-request.ts`.
 *
 * "Injective by construction" was previously claimed on the strength of (2)
 * alone, which was not a proof: without (1) there is no separator to be absent
 * from. Widening either half requires re-deriving the other; see the widening
 * note on `canonicalImageQuality`.
 */
const VARIANT_KEY_TEMPLATE =
  /^[a-z0-9_-]*\{[a-z][a-z0-9_]*\}(?:[a-z0-9_-]*[_-][a-z0-9_-]*\{[a-z][a-z0-9_]*\})*[a-z0-9_-]*$/;

const VARIANT_KEY_PLACEHOLDER = /\{([a-z][a-z0-9_]*)\}/g;

/**
 * The alphabet a canonical variant VALUE may use, and therefore the alphabet
 * the derivation probe below is drawn from.
 *
 * It is the second half of the injectivity premise stated on
 * `VARIANT_KEY_TEMPLATE`: the separator between adjacent placeholders is only
 * a separator while no admitted value can contain one. The gateway's
 * producers narrow to exactly this (`CANONICAL_VARIANT_VALUE` in
 * `tasks/unit-metering.ts`, `canonical-request.ts`), and it is restated here
 * because the derivation has to substitute something a real request could
 * actually produce.
 */
const VARIANT_VALUE_PROBE = '0';

/**
 * Does every key this template can produce satisfy the PRICE table's own
 * `variant_key` rule (#1937)?
 *
 * ── THE INCONSISTENCY THIS REPLACES, AND WHAT IT COST ─────────────────────
 *
 * `VARIANT_KEY_TEMPLATE` above admits `_` in literals and in placeholder
 * names. {@link UNIT_VARIANT_KEY_RE} — the rule the PRICE write faces enforce
 * on a `variant_key` column — does not: it is the channel-slug charset,
 * `[a-z0-9][a-z0-9-]*`. Two regexes, two write faces, one disagreement, and
 * the failure it produced is silent at both of them and loud only in
 * production: a `{size}-{quality}` spec is accepted by the metering write
 * face, the matching price rows are refused 400 by the pricing write face,
 * and every request then resolves a tier that has no price row — refused at
 * the route gate as `variant_unpriced`, i.e. the SKU registers successfully
 * and serves nothing, with the two 400s in different operator sessions.
 *
 * ── WHY A DERIVATION AND NOT A THIRD REGEX ────────────────────────────────
 *
 * A narrowed copy of `VARIANT_KEY_TEMPLATE` would be a second parallel rule
 * that has to be re-derived every time either alphabet moves — the same
 * defect one layer along. Substituting a probe value and asking the PRICE
 * rule itself makes the price rule the single authority: widen
 * `UNIT_VARIANT_KEY_RE` and this follows, with no edit here.
 *
 * ── WHAT THE PROBE DOES AND DOES NOT PROVE ────────────────────────────────
 *
 * It proves the ALPHABET and the ANCHORS: every literal character of the
 * template, and the fact that the product starts with `[a-z0-9]`. It does NOT
 * prove the 63-character LENGTH bound, because that depends on runtime values
 * the validator has not seen. That residue is fail-closed rather than
 * unhandled: an over-long resolved key simply has no price row, so the route
 * gate refuses it with `variant_unpriced` before any upstream spend, which is
 * the same recoverable 400 this check produces at write time.
 */
function templateProducesLegalVariantKeys(template: string): boolean {
  const probed = template.replace(VARIANT_KEY_PLACEHOLDER, VARIANT_VALUE_PROBE);
  return UNIT_VARIANT_KEY_RE.test(probed);
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/**
 * Validate an untrusted candidate spec (a parsed
 * `billable_dimensions_json`, or an operator's write-face payload).
 *
 * Rejects rather than repairs, and reports EVERY violation it finds: the
 * caller is a 400 response whose reader has to fix the document, and a
 * one-at-a-time validator turns that into a guessing loop.
 *
 * Unknown keys are a violation, not ignored data. A spec is a money document;
 * a misspelled `varaint_key_template` that validates and then does nothing is
 * the silent-undercharge shape ADR-012 §3 exists to exclude.
 *
 * On success the returned `spec` is a NEW, normalized object built field by
 * field from what was validated — never the caller's object. Round two caches
 * a validated spec in the pub snapshot and reuses it across requests, so
 * handing back a reference into a parsed request body would put a caller-owned
 * mutable object on a shared billing path.
 */
export function validateMeteringSpec(value: unknown): MeteringSpecValidation {
  if (!isPlainObject(value)) return { ok: false, violations: ['not_an_object'] };
  // DISPATCH ON THE LANE FIRST (#1937). The two lanes admit disjoint key sets,
  // so running one validator over the other's document reports a cascade of
  // violations about keys that were never supposed to be there — and, worse,
  // would let a `lane: 'token'` document be assessed as a unit spec, which is
  // exactly the bridge ADR-012 §4 forbids. An unknown lane word is answered
  // once, here, rather than by whichever branch happened to run.
  const lane = KNOWN_METERING_LANES.find((candidate) => candidate === value['lane']);
  if (lane === undefined) return { ok: false, violations: ['unknown_lane'] };
  return lane === 'token' ? validateTokenLaneSpec(value) : validateUnitLaneSpec(value);
}

/**
 * The token lane's whole validator (#1937): a version, a lane, an optional
 * protocol list, and a REQUIRED `variant_key_template`.
 *
 * The unit-lane keys are refused by name rather than ignored. A document that
 * carried a `quantity` and had it silently dropped would read to its author as
 * a quantity rule that is in force — and the one thing this lane must never
 * acquire is a quantity an operator can write.
 */
function validateTokenLaneSpec(value: Record<string, unknown>): MeteringSpecValidation {
  const violations: MeteringSpecViolation[] = [];
  for (const key of Object.keys(value)) {
    if (TOKEN_LANE_SPEC_KEYS.has(key)) continue;
    violations.push(SPEC_KEYS.has(key) ? 'lane_key_not_admitted' : 'unknown_key');
  }
  if (value['spec_version'] !== 1) violations.push('unknown_spec_version');
  const declaredProtocols = validateProtocols(value['protocols'], violations);

  const rawTemplate = value['variant_key_template'];
  const template = rawTemplate === undefined
    ? undefined
    : validateVariantKeyTemplate(rawTemplate, violations);
  if (rawTemplate === undefined) violations.push('token_template_required');

  if (violations.length > 0 || declaredProtocols === null || template === undefined) {
    return { ok: false, violations: [...new Set(violations)] };
  }
  // A NEW object, field by field, for the same reason the unit branch builds
  // one: a validated spec is cached in the pub snapshot and shared across
  // requests, so it must not be a reference into a parsed request body.
  return {
    ok: true,
    spec: {
      spec_version: 1,
      lane: 'token',
      ...(declaredProtocols === undefined ? {} : { protocols: [...declaredProtocols] }),
      variant_key_template: template,
    },
  };
}

/**
 * A `variant_key` template, or `undefined` when it was declared illegally.
 *
 * THREE checks, and the third is the #1937 repair: structural legality (the
 * injectivity premise), placeholder names against the closed field set, and
 * — new — whether the keys it can produce are spellable in the PRICE table.
 * The third used to be absent, so the metering write face and the pricing
 * write face disagreed about `_` and a SKU could register successfully while
 * every one of its price rows was refused.
 */
function validateVariantKeyTemplate(
  value: unknown,
  violations: MeteringSpecViolation[],
): string | undefined {
  if (typeof value !== 'string' || !VARIANT_KEY_TEMPLATE.test(value)) {
    violations.push('invalid_variant_key_template');
    return undefined;
  }
  if (!validatePlaceholders(value, violations)) return undefined;
  if (!templateProducesLegalVariantKeys(value)) {
    violations.push('variant_key_template_unpriceable');
    return undefined;
  }
  return value;
}

function validateUnitLaneSpec(value: Record<string, unknown>): MeteringSpecValidation {
  const violations: MeteringSpecViolation[] = [];

  for (const key of Object.keys(value)) {
    if (!SPEC_KEYS.has(key)) {
      violations.push('unknown_key');
      break;
    }
  }
  if (value['spec_version'] !== 1) violations.push('unknown_spec_version');
  const lane = 'unit' as const;
  const unit = KNOWN_UNIT_USAGE_UNITS.find((candidate) => candidate === value['unit']);
  if (unit === undefined) violations.push('unknown_unit');
  const gate = UNIT_DELIVERY_GATES.find((candidate) => candidate === value['delivery_gate']);
  if (gate === undefined) violations.push('unknown_delivery_gate');

  const declaredProtocols = validateProtocols(value['protocols'], violations);
  const quantity = validateTerm(value['quantity'], declaredProtocols, violations);
  const zeroingField = validateZeroingField(
    value['delivery_zeroing_field'],
    declaredProtocols,
    violations,
  );

  const rawTemplate = value['variant_key_template'];
  const template = rawTemplate === undefined
    ? undefined
    : validateVariantKeyTemplate(rawTemplate, violations);

  const rawSurcharge = value['surcharge'];
  const surcharge = rawSurcharge === undefined
    ? undefined
    : validateSurcharge(rawSurcharge, declaredProtocols, violations);

  const rawByVariant = value['quantity_by_variant'];
  let byVariant: Record<string, MeteringQuantityTerm> | undefined;
  // ADR-012 §4 precondition 1, enforced where it can be: the two keys are
  // MUTUALLY REQUIRED. A table without a template names tiers nothing can
  // resolve; a template without a table is an OPEN parameter surface priced on
  // the base term, i.e. every `WIDTHxHEIGHT` the canonicalizer admits billing
  // at one rate off an unbounded price table. The second direction is the one
  // that costs money, and it is the reason `KNOWN_VARIANT_KEY_FIELDS` could be
  // populated at all.
  if (rawTemplate !== undefined && rawByVariant === undefined) {
    violations.push('variant_template_without_coefficients');
  }
  if (rawByVariant !== undefined) {
    if (rawTemplate === undefined) violations.push('variant_coefficients_without_template');
    if (!isPlainObject(rawByVariant) || Object.keys(rawByVariant).length === 0) {
      violations.push('invalid_variant_coefficients');
    } else {
      const normalized: Record<string, MeteringQuantityTerm> = {};
      for (const [variantKey, term] of Object.entries(rawByVariant)) {
        if (variantKey === '') {
          violations.push('invalid_variant_coefficients');
          break;
        }
        const validated = validateTerm(term, declaredProtocols, violations);
        if (validated !== null) normalized[variantKey] = validated;
      }
      byVariant = normalized;
    }
  }

  if (
    violations.length > 0 ||
    unit === undefined ||
    gate === undefined ||
    quantity === null ||
    surcharge === null ||
    declaredProtocols === null ||
    zeroingField === null ||
    // NOT redundant with `violations.length > 0`. The spec returned below is
    // rebuilt field by field, so every optional key it can carry must appear
    // BOTH in the literal and in a guard that refuses to return a spec whose
    // key the rebuild would silently drop. Round one held the two variant keys
    // out of the literal and stated that condition here; round two moved them
    // into the literal, so what this clause now guards is the residue: a
    // template or a variant table that was PRESENT on the input and did not
    // survive validation (an illegal placeholder, an illegal term) must not
    // come back as a spec that quietly lost it — that would strip the template
    // off a spec that VALIDATED, blind the evaluator's exact-match rule, and
    // bill every declared tier at the `(unit, '')` default row.
    //
    // A half-done change therefore refuses with an EMPTY violation list. That
    // is deliberate: an empty-reason refusal is visibly broken and gets
    // noticed, where a violation code would dress the half-done state up as a
    // legitimate validation outcome.
    (rawTemplate === undefined) !== (template === undefined) ||
    (rawByVariant === undefined) !== (byVariant === undefined) ||
    (rawSurcharge === undefined) !== (surcharge === undefined)
  ) {
    return { ok: false, violations: [...new Set(violations)] };
  }
  // A NEW object, field by field — see the doc comment. Every optional key is
  // carried here and guarded above; adding one to the type means adding it to
  // both.
  return {
    ok: true,
    spec: {
      spec_version: 1,
      lane,
      unit,
      quantity,
      delivery_gate: gate,
      ...(zeroingField === undefined ? {} : { delivery_zeroing_field: zeroingField }),
      ...(declaredProtocols === undefined ? {} : { protocols: [...declaredProtocols] }),
      ...(template === undefined ? {} : { variant_key_template: template }),
      ...(byVariant === undefined ? {} : { quantity_by_variant: { ...byVariant } }),
      ...(surcharge === undefined ? {} : { surcharge }),
    },
  };
}

/**
 * Every placeholder must name a known variant field — a CLOSED set, so a
 * `{qualtiy}` typo is a refused document rather than a brand-new tier whose
 * price row never matches. Returns whether the template survived.
 */
function validatePlaceholders(template: string, violations: MeteringSpecViolation[]): boolean {
  for (const match of template.matchAll(VARIANT_KEY_PLACEHOLDER)) {
    const name = match[1];
    if (name === undefined || variantFieldNamed(name) === undefined) {
      violations.push('unknown_variant_key_field');
      return false;
    }
  }
  return true;
}

/**
 * The zeroing observation, or `null` when it was declared illegally.
 *
 * Grade is the whole check: an observation may cancel a charge (ADR-012 §2's
 * directional exception) and self-evidence in this seat would be the request
 * gating itself. The protocol rule is the same one terms obey — a fact that
 * only exists on some wires may only be read by a spec that declares them, or
 * it would silently read as "not delivered" everywhere else and cancel every
 * charge.
 */
function validateZeroingField(
  value: unknown,
  declaredProtocols: readonly ModelProtocol[] | null | undefined,
  violations: MeteringSpecViolation[],
): MeteringObservationDescriptorField | null | undefined {
  if (value === undefined) return undefined;
  const named = KNOWN_METERING_DESCRIPTOR_FIELDS.find((candidate) => candidate === value);
  const observation = named === undefined ? undefined : observationDescriptorFieldNamed(named);
  if (observation === undefined) {
    violations.push('invalid_delivery_zeroing_field');
    return null;
  }
  return protocolsCover(observation, declaredProtocols, violations) ? observation : null;
}

/**
 * A fact that only exists on some wires may only be read by a spec that has
 * declared it reads those wires — otherwise it would evaluate to "zero of a
 * fact that cannot exist" on every other protocol.
 */
function protocolsCover(
  field: MeteringDescriptorField,
  declaredProtocols: readonly ModelProtocol[] | null | undefined,
  violations: MeteringSpecViolation[],
): boolean {
  const admissible = DESCRIPTOR_FIELD_META[field].protocols;
  if (admissible === null) return true;
  if (
    declaredProtocols === undefined ||
    declaredProtocols === null ||
    !declaredProtocols.every((protocol) => admissible.includes(protocol))
  ) {
    violations.push('descriptor_protocol_undeclared');
    return false;
  }
  return true;
}

/** `undefined` = not declared; `null` = declared but illegal. */
function validateProtocols(
  value: unknown,
  violations: MeteringSpecViolation[],
): readonly ModelProtocol[] | null | undefined {
  if (value === undefined) return undefined;
  if (!Array.isArray(value) || value.length === 0) {
    violations.push('invalid_protocols');
    return null;
  }
  const known: ModelProtocol[] = [];
  for (const entry of value) {
    const protocol = KNOWN_MODEL_PROTOCOLS.find((candidate) => candidate === entry);
    if (protocol === undefined) {
      violations.push('invalid_protocols');
      return null;
    }
    known.push(protocol);
  }
  return known;
}

/** The normalized surcharge, or `null` when it was illegal (violations pushed). */
function validateSurcharge(
  value: unknown,
  declaredProtocols: readonly ModelProtocol[] | null | undefined,
  violations: MeteringSpecViolation[],
): { readonly unit: UnitUsageUnit; readonly quantity: MeteringQuantityTerm } | null {
  if (!isPlainObject(value)) {
    violations.push('invalid_surcharge');
    return null;
  }
  for (const key of Object.keys(value)) {
    if (key !== 'unit' && key !== 'quantity') {
      violations.push('invalid_surcharge');
      return null;
    }
  }
  const unit = KNOWN_UNIT_USAGE_UNITS.find((candidate) => candidate === value['unit']);
  if (unit === undefined) {
    violations.push('unknown_unit');
    return null;
  }
  const quantity = validateTerm(value['quantity'], declaredProtocols, violations);
  if (quantity === null) return null;
  return { unit, quantity };
}

/** The normalized term, or `null` when it was illegal (violations pushed). */
function validateTerm(
  value: unknown,
  declaredProtocols: readonly ModelProtocol[] | null | undefined,
  violations: MeteringSpecViolation[],
): MeteringQuantityTerm | null {
  if (!isPlainObject(value) || !isFiniteNumber(value['a']) || !isFiniteNumber(value['b'])) {
    violations.push('invalid_quantity_term');
    return null;
  }
  for (const key of Object.keys(value)) {
    if (!TERM_KEYS.has(key)) {
      violations.push('invalid_quantity_term');
      return null;
    }
  }
  // INTEGRALITY, checked here and nowhere else. See the note on
  // `MeteringQuantityTerm`: a fractional coefficient or bound produces a
  // fractional quantity for some inputs, and the kernel takes that as
  // `cost_overflow`, which is a SILENT zero on a delivered artifact. Reported
  // rather than returned on, so a document with several defects still gets one
  // round trip.
  let integral = true;
  if (!Number.isSafeInteger(value['a']) || !Number.isSafeInteger(value['b'])) {
    violations.push('non_integer_quantity_term');
    integral = false;
  }
  let bounded = true;
  for (const bound of ['min', 'max'] as const) {
    const declared = value[bound];
    if (declared === undefined) continue;
    if (!isFiniteNumber(declared)) {
      violations.push('invalid_bounds');
      bounded = false;
      continue;
    }
    if (!Number.isSafeInteger(declared)) {
      violations.push('non_integer_quantity_term');
      integral = false;
    }
  }
  const min = value['min'];
  const max = value['max'];
  if (isFiniteNumber(min) && isFiniteNumber(max) && min > max) {
    violations.push('invalid_bounds');
    bounded = false;
  }
  const bounds = {
    ...(isFiniteNumber(min) ? { min } : {}),
    ...(isFiniteNumber(max) ? { max } : {}),
  };

  const field = value['field'];
  if (field === undefined) {
    // Nothing to multiply: a non-zero coefficient means the author believed a
    // descriptor was being read, and shipping it would bill a constant.
    if (value['a'] !== 0) {
      violations.push('coefficient_without_descriptor');
      return null;
    }
    return bounded && integral ? { a: value['a'], b: value['b'], ...bounds } : null;
  }
  const named = KNOWN_METERING_DESCRIPTOR_FIELDS.find((candidate) => candidate === field);
  if (named === undefined) {
    violations.push('unknown_descriptor_field');
    return null;
  }
  // ADR-012 §2's admissibility matrix, priced row, as amended by #1883: a
  // priced quantity may read self-evidence or an admission-verified terminal
  // report. An OBSERVATION here is still the original §2 conflict — a
  // zeroing-only grade setting an amount. The TYPE already forbids it for a
  // spec written in this repo; this arm is what refuses it in an untrusted
  // stored document, which the type cannot reach.
  const pricedField = pricedDescriptorFieldNamed(named);
  if (pricedField === undefined) {
    violations.push('priced_descriptor_not_self_evidence');
    return null;
  }
  if (!protocolsCover(pricedField, declaredProtocols, violations)) return null;
  return bounded && integral
    ? { field: pricedField, a: value['a'], b: value['b'], ...bounds }
    : null;
}

/**
 * What the gateway knows about ONE request before it routes it — the whole of
 * the self-evidence a spec is allowed to price against (ADR-012 §2).
 *
 * `variant` carries the canonical values a `variant_key` template resolves
 * against; `fields` carries named descriptor observations, where an absent
 * entry means "not observed". Both are produced by the gateway from the
 * request, never copied out of the tenant's body.
 *
 * It is a SEPARATE type from the settlement descriptor on purpose: the route
 * gate takes this one, so it is structurally incapable of admitting a request
 * on the strength of a fact that only exists after the upstream answered.
 */
/**
 * The half of a request's self-evidence that decides its PRICE TIER, split out
 * so both lanes can be handed it (#1937).
 *
 * The unit lane's `UnitMeteringRequestFacts` extends it with the descriptor
 * fields a QUANTITY reads; the token lane has no such fields — its amount is
 * the upstream's counter — so it passes exactly this and nothing more. Keeping
 * the tier half addressable on its own is what lets `resolveVariantKey` be one
 * function rather than one per lane, which is the whole reason a token-lane
 * tier and a unit-lane tier cannot be resolved by two divergent rules.
 */
export interface MeteringVariantFacts {
  readonly variant: Readonly<Partial<Record<MeteringVariantField, string>>>;
  readonly variantEgress: MeteringVariantEgress;
}

export interface UnitMeteringRequestFacts extends MeteringVariantFacts {
  readonly protocol: ModelProtocol;
  readonly variant: Readonly<Partial<Record<MeteringVariantField, string>>>;
  /**
   * Whether the canonical values in `variant` were OVERWRITTEN into the body
   * this request will be dispatched with, on a route armed to honour them.
   *
   * Separate from `variant` on purpose: canonicalizing a tier and delivering
   * that tier are two different claims, and only the second licenses pricing
   * (ADR-012 §4 precondition 2). A producer that could not establish the
   * second must say `not_forwarded` while still reporting what it parsed —
   * the values remain useful for observability, and the route gate refuses to
   * price them.
   */
  readonly variantEgress: MeteringVariantEgress;
  /**
   * SELF-evidence only, in the type. The route gate reads this and nothing
   * else, so it cannot be handed a settlement-time observation, and the
   * settle's merge of request facts with response observations cannot collide
   * on a key — the two populations are disjoint types, not two halves of one
   * record kept apart by spread order.
   */
  readonly fields: Readonly<Partial<Record<MeteringSelfDescriptorField, number | undefined>>>;
}

/**
 * The facts one SETTLED request offers a spec: the request facts above plus
 * the four-axis forwarding proof and whatever observations the response
 * carried.
 */
export interface UnitMeteringDescriptor extends UnitMeteringRequestFacts {
  /** Both populations, once the response has been seen. */
  readonly fields: Readonly<Partial<Record<MeteringDescriptorField, number | undefined>>>;
  readonly delivered: boolean;
}

/** Why a spec cannot meter a request. Each maps to a route-gate refusal. */
export type UnitMeteringAdmissionRefusal =
  /** The ingress protocol is outside the spec's declared set. */
  | 'protocol_unsupported'
  /** A template placeholder had no canonical value for this request. */
  | 'variant_unresolved'
  /**
   * The tier was canonicalized but this request's EGRESS SHAPE does not carry
   * it to the upstream, so it is not self-evidence (ADR-012 §4 precondition
   * 2). Distinct from `variant_unresolved`: the value exists and is perfectly
   * canonical; the route is what cannot be priced on.
   */
  | 'variant_not_forwarded'
  /** A declared variant table has no coefficient entry for the resolved key. */
  | 'variant_uncovered'
  /** A priced term reads self-evidence this ingress does not produce. */
  | 'request_not_self_evidenced';

export type UnitMeteringAdmission =
  | {
      readonly ok: true;
      readonly variantKey: string;
      readonly term: MeteringQuantityTerm;
    }
  | { readonly ok: false; readonly reason: UnitMeteringAdmissionRefusal };

/**
 * Can this spec meter this request, and at which variant and coefficients?
 *
 * THE ROUTE GATE'S PREDICATE, and the evaluator's precondition — one function,
 * so the two cannot drift ON THE RULE. Most inputs are known at ingress,
 * before routing, so a refusal here costs a 400 and no upstream spend; the
 * same refusal discovered at settlement would mean the upstream was paid, the
 * artifact delivered and the event quarantined at $0, which is ADR-012 §3's
 * "loudly free" and the shape *Cost accepted* says should have been a REFUSED
 * request.
 *
 * ── They CAN drift on one input: `protocol`. Known, not fixed here. ────────
 *
 * The gate runs before routing and can only know the SKU's NATIVE protocol;
 * the evaluator runs at settlement and is given the protocol actually bought,
 * because that is what the response was spoken in. Cross-protocol routing
 * makes those two different values, and a spec with a `protocols` list can
 * therefore be admitted on the native protocol and then refuse at settlement —
 * no block, `invalid_usage` quarantine, $0. Loudly free: the exact shape the
 * gate exists to exclude, reached by the one input the gate cannot see.
 *
 * This PREDATES #1878 — the old `switch` had the same split, refusing a
 * non-openai image settle with `null` — and #1878 round two did not introduce
 * it. What round two briefly did do was describe it as impossible, which is
 * worse than the bug: a reader would have stopped looking. It is recorded
 * here instead, and `metering-spec.test.ts` pins the divergent cell (gate
 * openai, evaluator anthropic) with the CURRENT result, so the next person to
 * touch this finds a failing-shaped fact rather than a reassuring sentence.
 *
 * Not repaired in this segment because the repair is a routing-side decision,
 * not a metering one: either the gate must run again after a candidate is
 * pinned, or a unit-lane SKU must be barred from cross-protocol routing. Both
 * reach outside the billing path and belong with the segment that owns it.
 *
 * COVERAGE IS HALF-CHECKED HERE, and the other half is checked BY THE CALLER.
 * ADR-012 §3 requires both halves before admitting a templated request: a
 * coefficient entry for the resolved variant (checked below) AND a
 * `(unit, variant_key)` price row for it.
 *
 * The price half cannot be checked AT THIS SEAT — this function takes a spec
 * and request facts, and the price data is neither. #1878's platform segment
 * publishes the priced identities on `pub_model_unit_price_variants`
 * (migration 0037; identities and generation instants only, never amounts —
 * ADR-009 §3), `isPubUnitVariantPriced` in the gateway's
 * `pub-config/readers.ts` selects the same generation rating will, and the
 * route gate in `generation/unit-metering.ts` is where the two halves meet,
 * because only it holds the config snapshot. It applies the price check
 * exactly where `unitRateLookupModeFor` answers `exact_match_only` — §3's own
 * scoping, so an untemplated spec keeps today's ladder untouched.
 *
 * `computeUnitCostBreakdown`'s `unit_price_missing` quarantine sits BEHIND
 * that gate rather than in front of it: it still catches an unpriced variant
 * loudly at $0, but only after the upstream was paid and the artifact
 * delivered, which is the shape §3's *Cost accepted* says should have been a
 * refused request.
 *
 * CURRENT STATE IN ONE LINE, so "#1878" is not read as "still pending": the
 * spec write face is OPEN, the projection is DELIVERED, and the gateway READS
 * BOTH — a stored spec governs this predicate and the evaluator, and the
 * priced identities gate a templated request before any spend.
 */
export function admitUnitMeteringRequest(
  spec: UnitLaneMeteringSpec,
  facts: UnitMeteringRequestFacts,
): UnitMeteringAdmission {
  if (spec.protocols !== undefined && !spec.protocols.includes(facts.protocol)) {
    return { ok: false, reason: 'protocol_unsupported' };
  }
  const resolved = resolveVariantKey(spec.variant_key_template, facts);
  if (!resolved.ok) return { ok: false, reason: resolved.reason };
  const variantKey = resolved.key;
  // EXACT MATCH, never a fallback: see `quantity_by_variant`. A declared
  // variant table that does not cover the resolved key refuses, because
  // billing an uncovered tier on the default coefficients is the same silent
  // undercharge as billing it on the default price row.
  const term =
    spec.quantity_by_variant === undefined
      ? spec.quantity
      : spec.quantity_by_variant[variantKey];
  if (term === undefined) return { ok: false, reason: 'variant_uncovered' };
  // A SELF-priced term's value is absent rather than zero when the ingress
  // does not produce it, and reading it as zero would bill a delivered
  // request nothing — so its absence refuses HERE, at the route gate, before
  // any spend. A TERMINAL-REPORT term (#1883) is deliberately NOT required
  // here: its value exists only on the upstream's terminal document, so the
  // gate cannot demand it — the evaluator's own three-state guard is where a
  // missing one quarantines (no block, never a zero).
  const selfField = term.field === undefined ? undefined : selfDescriptorFieldNamed(term.field);
  if (selfField !== undefined && facts.fields[selfField] === undefined) {
    return { ok: false, reason: 'request_not_self_evidenced' };
  }
  return { ok: true, variantKey, term };
}

export type VariantKeyResolution =
  | { readonly ok: true; readonly key: string }
  | {
      readonly ok: false;
      readonly reason: Extract<
        UnitMeteringAdmissionRefusal,
        'variant_unresolved' | 'variant_not_forwarded'
      >;
    };

/**
 * Resolve the template against the canonical request values.
 *
 * A REFUSAL — not a partial key — when any placeholder cannot contribute. A
 * partial key would be a DIFFERENT tier that happens to be spellable, and the
 * price table would either miss it (loud) or, worse, contain it.
 *
 * TWO refusals, checked in this order per placeholder, and the order is what
 * makes them tell apart rather than shadow each other:
 *
 *   1. `variant_not_forwarded` — the field is not self-evidence on THIS
 *      request's egress shape (`VARIANT_FIELD_META`). Checked FIRST, so a
 *      bypass request carrying a perfectly canonical `1024x1024`+`high`
 *      refuses for the reason that is actually true of it. Were this checked
 *      second, such a request would be admitted whenever the values happened
 *      to be present, which is precisely the mispricing this gate exists to
 *      exclude.
 *   2. `variant_unresolved` — an unknown placeholder name, or no canonical
 *      value for a known one.
 *
 * The substitution reads `facts.variant`, never the tenant's body: the values
 * are produced by the gateway's canonicalizer (ADR-012 §2 self-evidence), so
 * nobody can hand-craft a key and steer which price row applies.
 *
 * `undefined` template is the no-variant case and resolves to `''`, which is
 * the `(unit, '')` default row by exact match.
 */
export function resolveVariantKey(
  template: string | undefined,
  facts: MeteringVariantFacts,
): VariantKeyResolution {
  if (template === undefined) return { ok: true, key: '' };
  let out = '';
  let cursor = 0;
  for (const match of template.matchAll(VARIANT_KEY_PLACEHOLDER)) {
    const name = match[1];
    if (name === undefined) return { ok: false, reason: 'variant_unresolved' };
    const field = variantFieldNamed(name);
    if (field === undefined) return { ok: false, reason: 'variant_unresolved' };
    if (!VARIANT_FIELD_META[field].egress.includes(facts.variantEgress)) {
      return { ok: false, reason: 'variant_not_forwarded' };
    }
    const value = facts.variant[field];
    if (value === undefined || value.length === 0) {
      return { ok: false, reason: 'variant_unresolved' };
    }
    out += template.slice(cursor, match.index) + value;
    cursor = match.index + match[0].length;
  }
  return { ok: true, key: out + template.slice(cursor) };
}

/**
 * The closed VALUE domain of each variant field, where one exists.
 *
 * `has_video_input` is a boolean rendered into exactly two constants
 * ({@link VIDEO_INPUT_VARIANT_VALUES}), so its domain is closed here. The
 * parameter-sourced fields (`size`, `quality`, `resolution`) have
 * operator-enumerated domains this file cannot know — the enumeration is the
 * registration's own data (the admission surface's enum rule, the spec's
 * `quantity_by_variant` key set) — so any non-empty canonical value is
 * admissible per segment and the exact-match price/coefficient lookups carry
 * the rest.
 */
const VARIANT_FIELD_VALUE_DOMAINS: Readonly<
  Partial<Record<MeteringVariantField, readonly string[]>>
> = {
  has_video_input: [VIDEO_INPUT_VARIANT_VALUES.absent, VIDEO_INPUT_VARIANT_VALUES.present],
};

/**
 * Could `resolveVariantKey` ever produce `key` from `template`, for ANY
 * canonical request values? — the REVERSE of the substitution above, for the
 * price write faces.
 *
 * A price row keyed outside the governing template is a DEAD ROW: the
 * substitution can never spell it, so it is never selected, while the tier the
 * operator believed they priced keeps refusing `variant_unpriced` at the route
 * gate. This helper is what lets a write face refuse that row at write time
 * with a 400 the operator can act on, instead of storing it silently.
 *
 * ONE derivation, not a second template grammar: the pattern is built by
 * walking the same `VARIANT_KEY_PLACEHOLDER` matches `resolveVariantKey`
 * substitutes into, a closed-domain field (see
 * {@link VARIANT_FIELD_VALUE_DOMAINS}) admits exactly its enumerated
 * constants, and an open field admits any non-empty `[a-z0-9]+` value — the
 * canonical value alphabet, i.e. the second half of the injectivity premise on
 * {@link VARIANT_KEY_TEMPLATE}, which is also what makes the template's
 * literal separators structural: a value cannot contain one, so the key's
 * segment count and separators must match the template's exactly.
 *
 * FAIL-CLOSED on a template this file's validator would refuse (structurally
 * illegal, or naming an unknown field): no key matches it, because nothing
 * could ever be resolved from it either.
 */
export function variantKeyMatchesTemplate(template: string, key: string): boolean {
  if (!VARIANT_KEY_TEMPLATE.test(template)) return false;
  let pattern = '';
  let cursor = 0;
  for (const match of template.matchAll(VARIANT_KEY_PLACEHOLDER)) {
    const name = match[1];
    const field = name === undefined ? undefined : variantFieldNamed(name);
    if (field === undefined) return false;
    const domain = VARIANT_FIELD_VALUE_DOMAINS[field];
    // Literal slices need no escaping: VARIANT_KEY_TEMPLATE (tested above)
    // admits only `[a-z0-9_-]` between placeholders, none of which is a regex
    // metacharacter.
    pattern += template.slice(cursor, match.index);
    pattern += domain === undefined ? '[a-z0-9]+' : `(?:${domain.join('|')})`;
    cursor = match.index + match[0].length;
  }
  pattern += template.slice(cursor);
  return new RegExp(`^(?:${pattern})$`).test(key);
}

/**
 * The per-field values `resolveVariantKey` substituted into `key`, or `null`
 * when the key is not one this template can spell — the INVERSE of the
 * substitution, for the two seats that hold a RESOLVED key and need its
 * fields back:
 *
 *   - the console's per-field tier controls, prefilling an existing price
 *     row's fields so the operator edits values, never respells a key;
 *   - the gateway's task settlement (#1883 final sub-item): a unit-lane task
 *     spec whose template names `has_video_input` prices a fact read from
 *     `input.content` at the submit gate, and the content is gone by
 *     settlement — only its fingerprint is stored. The admitted tier key is
 *     frozen on the task row (the same migration-0016 freeze the token lane uses),
 *     and this function recovers the field values from it so settlement's
 *     re-resolution reproduces the SAME key through the same rule, rather
 *     than a second resolution path existing for the task lane.
 *
 * Unambiguous by the injectivity premise on {@link VARIANT_KEY_TEMPLATE}:
 * values are `[a-z0-9]+` and adjacent placeholders are separated by at least
 * one `[_-]`, which no value can contain, so each captured segment is exactly
 * one field's value. Capture is deliberately the OPEN alphabet even for a
 * closed-domain field: the caller holds a key that was already admitted and
 * priced, and refusing to invert it over a domain widening would strand the
 * stored row — `variantKeyMatchesTemplate` above is the write-time domain
 * gate, and this is a read of what it admitted.
 *
 * A repeated placeholder keeps the LAST occurrence's capture; a template this
 * file's validator would refuse (structurally illegal, unknown field) inverts
 * to `null`, because nothing could have been resolved from it either.
 */
export function variantValuesFromKey(
  template: string,
  key: string,
): Readonly<Partial<Record<MeteringVariantField, string>>> | null {
  if (!VARIANT_KEY_TEMPLATE.test(template)) return null;
  let pattern = '';
  let cursor = 0;
  const fields: MeteringVariantField[] = [];
  for (const match of template.matchAll(VARIANT_KEY_PLACEHOLDER)) {
    const name = match[1];
    const field = name === undefined ? undefined : variantFieldNamed(name);
    if (field === undefined) return null;
    fields.push(field);
    pattern += template.slice(cursor, match.index);
    pattern += '([a-z0-9]+)';
    cursor = match.index + match[0].length;
  }
  pattern += template.slice(cursor);
  const matched = new RegExp(`^${pattern}$`).exec(key);
  if (matched === null) return null;
  const values: Partial<Record<MeteringVariantField, string>> = {};
  fields.forEach((field, index) => {
    const value = matched[index + 1];
    if (value !== undefined) values[field] = value;
  });
  return values;
}

/**
 * Evaluate a spec against one settled request.
 *
 * Returns `null` — author NO block — whenever the spec cannot be evaluated
 * truthfully. That is the loud path: the absent block quarantines
 * platform-side as `invalid_usage`, never a silent zero. Every `null` here is
 * defence in depth behind `admitUnitMeteringRequest` at the route gate, which
 * refuses the same requests before any upstream is touched.
 *
 * ORDER MATTERS, and is deliberate: a failed forwarding proof — or a closed
 * zeroing gate — zeroes the quantity WITHOUT reading the term at all, which
 * means it also bypasses a declared `min`. `min` is an allowance floor for a
 * request that happened; an undelivered request did not happen, and floor-ing
 * it to the allowance would bill the floor for every 502 — the `min` would
 * become a charge for failure. The zero then flows into the delivery gate
 * below, so a `positive_quantity` spec reports `delivered: false` and the
 * platform kernel takes its whitelisted `unit_not_delivered` zero.
 */
export function evaluateUnitMeteringSpec(
  spec: UnitLaneMeteringSpec,
  descriptor: UnitMeteringDescriptor,
): UnitUsageDetails | null {
  const admission = admitUnitMeteringRequest(spec, descriptor);
  if (!admission.ok) return null;
  // ADR-012 §2's directional exception, and the ONLY direction it runs in: an
  // observation joins the forwarding proof as a condition for reading the term
  // at all, so it can cancel a charge and can never set, raise or restore one.
  //
  // THREE STATES, not two. A spec that declares a zeroing field and is handed
  // NO value for it on this settle cannot be evaluated truthfully: "the gate
  // was measured and it was shut" and "the gate could not be measured at all"
  // are different facts, and collapsing them writes `quantity: 0` — which the
  // platform kernel takes as the whitelisted `unit_not_delivered` zero, i.e.
  // silently free and indistinguishable from a soft refusal.
  //
  // So an absent OBSERVATION for a DECLARED gate authors no block and
  // quarantines loudly as `invalid_usage`. A soft refusal is unaffected: that
  // is a measured zero (the gateway counted the payloads in a body it held and
  // found none), which is `0`, not absent. Every settle site is required to
  // tell the two apart — a wire that can carry the fact passes `0` when it did
  // not, and only a transport on which the fact is unobservable passes
  // nothing.
  const zeroingField = spec.delivery_zeroing_field;
  const observed = zeroingField === undefined ? undefined : descriptor.fields[zeroingField];
  if (zeroingField !== undefined && observed === undefined) return null;
  const gateOpen = zeroingField === undefined || (observed ?? 0) > 0;
  // A TERMINAL-REPORT priced term (#1883) has its own three-state guard, the
  // mirror of the zeroing field's: "the report stated a figure" and "the
  // report stated none" are different facts, and collapsing them would write
  // `quantity: 0` — the whitelisted `unit_not_delivered` zero — for a
  // delivered artifact whose amount we simply could not source. NO block is
  // authored instead, which quarantines loudly, and the caller never falls
  // back to the frozen order. Checked only where the term would actually be
  // read: an undelivered or gate-closed settle zeroes the charge without
  // needing the figure, which is a legitimate zero, not a missing one.
  const wouldRead = descriptor.delivered && gateOpen;
  const terminalField = admission.term.field === undefined
    ? undefined
    : terminalReportDescriptorFieldNamed(admission.term.field);
  if (wouldRead && terminalField !== undefined && descriptor.fields[terminalField] === undefined) {
    return null;
  }
  const quantity = wouldRead ? evaluateTerm(admission.term, descriptor.fields) : 0;
  return {
    unit: spec.unit,
    quantity,
    variant_key: admission.variantKey,
    delivered: spec.delivery_gate === 'positive_quantity' ? quantity > 0 : descriptor.delivered,
  };
}

/**
 * Evaluate the optional second unit term, or `null` when no surcharge
 * block should be authored (#2149).
 *
 * `null` here is NOT unevaluable: a job with zero images emits no
 * surcharge block at all, and that event must be byte-identical to the
 * pre-surcharge build. The caller treats "images present AND spec has no
 * surcharge" as unevaluable on its own — this function cannot tell that
 * case from "no images, no block".
 *
 * Quantity comes from the request-side count; it does not shrink with a
 * short delivery. `delivered: false` zeroes the quantity the same way
 * the primary term does (the images were consumed only if the job ran,
 * and a failed delivery absorbs the whole charge).
 *
 * Always `variant_key: ''`: there is no variant to price.
 */
export function evaluateUnitSurcharge(
  spec: UnitLaneMeteringSpec,
  descriptor: UnitMeteringDescriptor,
): UnitUsageDetails | null {
  if (spec.surcharge === undefined) return null;
  const field = spec.surcharge.quantity.field;
  const count = field === undefined ? spec.surcharge.quantity.b : descriptor.fields[field];
  if (count === undefined || count <= 0) return null;
  const zeroingField = spec.delivery_zeroing_field;
  const observed = zeroingField === undefined ? undefined : descriptor.fields[zeroingField];
  if (zeroingField !== undefined && observed === undefined) return null;
  const gateOpen = zeroingField === undefined || (observed ?? 0) > 0;
  const wouldRead = descriptor.delivered && gateOpen;
  const quantity = wouldRead ? evaluateTerm(spec.surcharge.quantity, descriptor.fields) : 0;
  // A delivered job whose surcharge term floors to 0 (MiniMax A's first
  // five reference images are free: `a:1, b:-5, min:0`) must not author
  // a block. Rating treats surcharge `delivered !== true` as whole-event
  // `unit_not_delivered`, which would also zero the seconds. Matching
  // the zero-image shape (no block) keeps the seconds. An undelivered
  // job still authors quantity 0 / delivered false so the inherited
  // gate stays visible — Grok's settlement test pins that shape, and
  // Grok's `a:1, b:0` never floors a delivered count to 0.
  if (quantity === 0 && wouldRead) return null;
  return {
    unit: spec.surcharge.unit,
    quantity,
    variant_key: '',
    delivered: spec.delivery_gate === 'positive_quantity' ? quantity > 0 : descriptor.delivered,
  };
}

function evaluateTerm(
  term: MeteringQuantityTerm,
  fields: Readonly<Partial<Record<MeteringDescriptorField, number | undefined>>>,
): number {
  const observed = term.field === undefined ? 0 : fields[term.field] ?? 0;
  const raw = term.a * observed + term.b;
  const floored = term.min === undefined ? raw : Math.max(raw, term.min);
  return term.max === undefined ? floored : Math.min(floored, term.max);
}

/**
 * The built-in specs, one per unit profile that bills today.
 *
 * These carry the incumbent behaviour: `model_metering_profiles
 * .billable_dimensions_json` is NULL on every row in existence, so without
 * them the two profiles with settled billing history would lose their
 * extractor the moment the `switch` went away. They are the reference the
 * equivalence golden pins.
 *
 * The two absent kinds are absent on purpose. `duration_seconds_v1` and
 * `text_characters_v1` bill quantities no integrated upstream reports yet, so
 * there is nothing truthful to compute; "no spec" is what the route gate reads
 * as `extractor_unimplemented` and turns into 400 `MODEL_BILLING_UNAVAILABLE`,
 * exactly as the hardcoded `IMPLEMENTED_UNIT_EXTRACTORS` set did before.
 *
 * ── How a stored spec joins (landed: `unitMeteringSpecFrom`) ──────────────
 *
 * **A stored spec REPLACES the built-in entirely; it never merges with it and
 * an illegal one never falls back to it.** The resolution is three-state and
 * the type makes the fallback unwritable — see `StoredMeteringSpecLookup`,
 * `resolveStoredMeteringSpec` and `unitMeteringSpecFrom` below.
 *
 * ── Variant coverage is checked at the ROUTE GATE, not here ───────────────
 *
 * Once a spec declares a `variant_key` template, "is this variant covered?"
 * is a precondition of serving the request, not a settlement-time discovery.
 * `admitUnitMeteringRequest` is that predicate and the gateway's route gate
 * runs it before routing; the evaluator's `null` on an uncovered variant is
 * DEPTH behind it, because by the time it fires the upstream has been paid and
 * the artifact delivered. Its remaining half — the `(unit, variant_key)` price
 * row — was the platform segment's and has landed as DATA: migration 0037
 * publishes the priced identities and `isPubUnitVariantPriced` reads them for
 * the route gate, which checks the coefficient entry and the price row
 * together. Why the check belongs to the route gate rather than to either
 * function here is recorded on `admitUnitMeteringRequest`.
 *
 * ── The provenance repair for `openai_images_unit_v1` (done) ───────────────
 *
 * The incumbent spec priced off THIRD-party evidence on the unit lane, which
 * ADR-012 §2/§4 forbid. It now has the shape §2 describes — quantity from
 * self-evidence, delivery from an observation used only as a zeroing gate:
 *
 *     quantity      ← `images_requested`, the canonical image count the
 *                     `/v1/images` ingress produces. Native Images admits
 *                     official `n` 1–10; the Codex / Responses bypass still
 *                     admits exactly one (`imagesToResponses.ts`
 *                     `rejectUnsupported`). A priced count other than 1
 *                     cannot be delivered on the bypass, let alone billed.
 *     delivery gate ← `image_payloads_delivered`, a FIRST-party count.
 *
 * The gate is first-party, not the `image_gen` fallback, and that was the
 * required order rather than a preference (§2: the fallback "is admissible
 * only if that is shown to be impossible on this wire"). It is not impossible
 * here: the unit-lane image SKU is reachable only through `/v1/images/*`,
 * whose response the gateway buffers whole and hands to the tenant as
 * `data[].b64_json` — it counts the payloads it is itself returning. (The
 * other two image doors, `/v1/responses` and `/v1/chat/completions` with the
 * built-in tool, carry no ordered image count, so a spec priced on
 * `images_requested` is refused there by the route gate before any spend
 * rather than served on someone else's assertion.)
 *
 * For every count the ingress admits this bills the same amount as before —
 * a provenance correction, not a repricing, which is why it is safe to do to a
 * profile with settled billing history. It is NOT output-identical on one
 * cell: a request whose response carried MORE image payloads than were ordered
 * now bills the ordered count. See the equivalence golden, which records that
 * divergence rather than hiding it.
 */
const BUILTIN_UNIT_METERING_SPECS: Readonly<
  Partial<Record<UnitMeteredProfileKind, UnitLaneMeteringSpec>>
> = {
  // One answered call is one unit; there is nothing to read off the response,
  // so the term is the constant 1 and delivery is the forwarding proof itself.
  flat_call_v1: {
    spec_version: 1,
    lane: 'unit',
    unit: 'call',
    quantity: { a: 0, b: 1 },
    delivery_gate: 'forwarding_proof',
  },
  // Bills the images the request ORDERED, gated by the images the gateway
  // itself counted in the response body it returned (#1668, repaired in
  // #1878 — see the provenance note above). A soft refusal passes the
  // forwarding proof and delivers no payload: the zeroing gate closes, the
  // quantity is zero, and `positive_quantity` settles `delivered: false`.
  openai_images_unit_v1: {
    spec_version: 1,
    lane: 'unit',
    unit: 'image',
    protocols: ['openai'],
    quantity: { field: 'images_requested', a: 1, b: 0 },
    delivery_zeroing_field: 'image_payloads_delivered',
    delivery_gate: 'positive_quantity',
  },
  // Bills the images the vendor actually returned (`data.length`), not
  // request `n`. Empty `data` on 2xx is quantity 0 → not delivered.
  grok_image_unit_v1: {
    spec_version: 1,
    lane: 'unit',
    unit: 'image',
    protocols: ['grok_image_v1'],
    quantity: { field: 'delivered_image_count', a: 1, b: 0 },
    delivery_gate: 'positive_quantity',
  },
};

/**
 * The built-in spec for a unit profile, or `null` when it has none.
 *
 * `null` is the fail-closed verdict the route gate turns into 400
 * `MODEL_BILLING_UNAVAILABLE`, so adding an entry here is the deliberate act
 * that turns a profile's SKUs on.
 */
export function builtinUnitMeteringSpec(
  profile: UnitMeteredProfileKind,
): UnitLaneMeteringSpec | null {
  return BUILTIN_UNIT_METERING_SPECS[profile] ?? null;
}

/**
 * What a SKU's stored spec column says — the three-state the SEAM on
 * `builtinUnitMeteringSpec` describes, now a real type.
 *
 * `present: false` is "the column held no document", which is every row in
 * existence today and resolves to the built-in. `present: true` with a spec is
 * that spec and ONLY that spec. `present: true` with `null` is a document that
 * failed validation, and it resolves to NO spec — never to the built-in.
 *
 * The union exists so `?? builtinUnitMeteringSpec(profile)` is unspellable at
 * the resolution seat. That fallback is strictly worse than having no spec: a
 * SKU whose stored spec declares `{size}-{quality}` but fails validation would
 * drop back to the built-in, emit `variant_key: ''`, hit the `(unit, '')`
 * default price row, and bill a delivered 4K-high image at the low tier. "No
 * spec" is a refused request, which is recoverable; that is a delivered
 * request with a silent order-of-magnitude discount, which is not.
 */
export type StoredMeteringSpecLookup =
  | { readonly present: false }
  | { readonly present: true; readonly spec: MeteringSpec | null };

/**
 * Read one `model_metering_profiles.billable_dimensions_json` value.
 *
 * The column is TEXT (0001 baseline) and the driver hands back a string or
 * `null`, but a non-string object is accepted too so that the day the column
 * becomes `jsonb` this reader does not silently start answering
 * `present: true, spec: null` for every row.
 *
 * EVERY present-but-unreadable form resolves to `present: true, spec: null`,
 * which is the refusing state — an empty string, whitespace, unparseable JSON,
 * a JSON scalar, a document the validator rejects. None of them is "no
 * document": a write face that validated could not have produced any of them,
 * so their appearance means someone wrote past it, and the recoverable
 * direction is to refuse rather than to fall back to a spec the operator did
 * not choose.
 */
export function resolveStoredMeteringSpec(value: unknown): StoredMeteringSpecLookup {
  if (value === null || value === undefined) return { present: false };
  if (typeof value !== 'string') {
    const validated = validateMeteringSpec(value);
    return { present: true, spec: validated.ok ? validated.spec : null };
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch {
    return { present: true, spec: null };
  }
  const validated = validateMeteringSpec(parsed);
  return { present: true, spec: validated.ok ? validated.spec : null };
}

/**
 * The spec that GOVERNS a unit profile once its stored column has been read:
 * the stored one when there is one, the built-in when the column was empty,
 * and `null` — no spec at all — when the stored document was illegal.
 *
 * `null` is the same fail-closed verdict `builtinUnitMeteringSpec` answers for
 * a profile with no built-in: the route gate turns it into 400
 * `MODEL_BILLING_UNAVAILABLE` before any upstream spend.
 */
export function unitMeteringSpecFrom(
  profile: UnitMeteredProfileKind,
  stored: StoredMeteringSpecLookup,
): UnitLaneMeteringSpec | null {
  if (!stored.present) return builtinUnitMeteringSpec(profile);
  // A stored spec of the WRONG LANE resolves to no spec, never to the built-in
  // (#1937). A `lane: 'token'` document on a unit-metered profile is an
  // operator error, and the recoverable direction is the same one an illegal
  // document takes: refuse at the route gate, rather than fall back to a spec
  // nobody chose and bill the default price row.
  return stored.spec !== null && stored.spec.lane === 'unit' ? stored.spec : null;
}

/**
 * The TOKEN-lane spec that governs a registration, or `null` when there is
 * none (#1937).
 *
 * ── THERE IS NO BUILT-IN, AND THAT ASYMMETRY IS THE DEFAULT BEHAVIOUR ─────
 *
 * `null` here means "this SKU declares no price tiers", which is what every
 * token SKU in existence says and must keep saying: the pricing generation's
 * single base row prices it, exactly as before. That is the opposite of the
 * unit lane, where `null` is a refusal — because on the unit lane a spec is
 * what produces the AMOUNT, and here it only names an axis.
 *
 * So the fail-closed behaviour lives one level up, on the SPECIFIC pair
 * (declared template, missing price row), and not on absence. Reading absence
 * as a refusal would take every token SKU in production off the air.
 *
 * A stored document of the wrong lane, or one that failed validation, also
 * answers `null` — and that IS the dangerous direction, because it silently
 * reverts a tiered SKU to single-rate pricing. It is caught elsewhere:
 * `tokenVariantLookupModeFor` answers `exact_match_only` for a stored document
 * that could not be read, so the price lookup refuses instead of falling back.
 * The two functions disagree deliberately, and the disagreement is the safety.
 */
export function tokenMeteringSpecFrom(
  stored: StoredMeteringSpecLookup,
): TokenLaneMeteringSpec | null {
  if (!stored.present || stored.spec === null) return null;
  return stored.spec.lane === 'token' ? stored.spec : null;
}

/**
 * ADR-012 §3's exact-match rule for the TOKEN lane: may the `variant_key: ''`
 * base row serve as a fallback for a generation that has variant rows?
 *
 * The three arms mirror `unitRateLookupModeFor` and for the same reasons:
 *
 *   - no stored document → `default_row_fallback`. Nothing was declared, so
 *     today's single-rate ladder stands. This is every token SKU shipping.
 *   - a token spec WITH a template → `exact_match_only`. Declaring tiers
 *     forbids the base row, so an unpriced tier quarantines instead of
 *     billing at whichever tier the base row happens to hold. On this lane
 *     the measured spread is 46/28 and its SIGN flips with traffic mix, so a
 *     fallback is not a conservative approximation in either direction.
 *   - a stored document that could not be read → `exact_match_only`, the
 *     quarantining direction. Whether it declared a template is exactly what
 *     is unknowable, and platform rates events the gateway settled earlier —
 *     so a SKU that was tiered at settle time and whose document broke
 *     afterwards must not fall back onto a base row sized for a different
 *     tier.
 */
export function tokenVariantLookupModeFor(
  stored: StoredMeteringSpecLookup,
): UnitRateLookupMode {
  if (!stored.present) return 'default_row_fallback';
  if (stored.spec === null) return 'exact_match_only';
  return stored.spec.lane === 'token' ? 'exact_match_only' : 'default_row_fallback';
}

/** Why a token-lane request's price tier could not be resolved. */
export type TokenMeteringAdmissionRefusal = Extract<
  UnitMeteringAdmissionRefusal,
  'protocol_unsupported' | 'variant_unresolved' | 'variant_not_forwarded'
>;

export type TokenMeteringAdmission =
  | { readonly ok: true; readonly variantKey: string }
  | { readonly ok: false; readonly reason: TokenMeteringAdmissionRefusal };

/**
 * The TOKEN lane's route-gate predicate: which price tier is this request in?
 *
 * Deliberately narrower than `admitUnitMeteringRequest`. There is no
 * coefficient table to cover and no priced descriptor to check — the amount
 * is the upstream's counter — so the only questions are whether the spec
 * speaks this protocol and whether the template resolves against canonical
 * request values that this egress shape actually carries.
 *
 * `variant_uncovered` is therefore absent from the refusal union, and its
 * absence is not an oversight: on the unit lane the spec's own
 * `quantity_by_variant` key set IS the enumeration of admissible tiers, and
 * here that table does not exist. The enumeration on this lane is the PRICE
 * table — the caller checks a published `(model, variant_key)` identity and
 * refuses `variant_unpriced` before any spend — which is the same
 * "declared-and-priced or refused" shape reached through one door instead of
 * two.
 */
export function admitTokenMeteringRequest(
  spec: TokenLaneMeteringSpec,
  facts: UnitMeteringRequestFacts,
): TokenMeteringAdmission {
  if (spec.protocols !== undefined && !spec.protocols.includes(facts.protocol)) {
    return { ok: false, reason: 'protocol_unsupported' };
  }
  const resolved = resolveVariantKey(spec.variant_key_template, facts);
  return resolved.ok ? { ok: true, variantKey: resolved.key } : { ok: false, reason: resolved.reason };
}

/**
 * ADR-012 §3's money rule, as the one bit the price lookup needs: may the
 * `(unit, '')` default row serve as a fallback for this SKU?
 *
 * THE SINGLE DERIVATION OF THAT BIT. `computeUnitCostBreakdown` consumes the
 * verdict and never re-derives it, so platform rating and the gateway's
 * coverage gate read one rule rather than two implementations of a sentence.
 *
 * THE TRIGGER IS A DECLARATION, NOT AN ABSENCE, and the difference decides
 * the two "no spec" arms below. §3's rule reads "a spec that DECLARES a
 * `variant_key` template forbids the `(unit, '')` default row" — so where
 * nothing was ever declared, today's ladder stands. Reading absence as a
 * declaration would have changed the incumbent behaviour of every unit SKU
 * without a built-in, which is not a rule this ADR states.
 *
 *   - a governing spec with NO template → `default_row_fallback`, i.e. the
 *     incumbent two-level ladder, byte-for-byte. Both built-ins are this case,
 *     which is why the rule is invisible to every SKU that bills today.
 *   - a governing spec WITH a template → `exact_match_only`. §3: declaring
 *     tiers forbids the default row, so an unpriced tier quarantines as
 *     `unit_price_missing` instead of billing the low tier's rate.
 *   - `present: false` with no built-in → `default_row_fallback`. Nothing was
 *     declared, and such a SKU is refused at the route gate anyway, so the
 *     arm is inert; it answers this way because the incumbent behaviour is
 *     what an inert arm should preserve.
 *   - `present: true, spec: null` — a stored document that cannot be read →
 *     `exact_match_only`. Here someone DID declare something, and whether it
 *     declared a template is exactly what is unknowable. The dangerous
 *     direction is real and not merely theoretical: platform rates events the
 *     gateway settled EARLIER, so a SKU whose spec was legal and templated at
 *     settle time (the block carries a real `variant_key`) and whose stored
 *     document was broken afterwards would otherwise bill that tier at the
 *     `(unit, '')` default row — §3's silent order-of-magnitude undercharge,
 *     on a delivered artifact.
 */
export function unitRateLookupModeFor(
  profile: UnitMeteredProfileKind,
  stored: StoredMeteringSpecLookup,
): UnitRateLookupMode {
  // A stored document that could not be read, OR one written for the OTHER
  // lane (#1937): in both cases someone DID declare something and whether it
  // declared tiers is exactly what is unknowable here, so the quarantining
  // direction is the only safe one.
  if (stored.present && (stored.spec === null || stored.spec.lane !== 'unit')) {
    return 'exact_match_only';
  }
  const spec = unitMeteringSpecFrom(profile, stored);
  if (spec === null) return 'default_row_fallback';
  return spec.variant_key_template === undefined ? 'default_row_fallback' : 'exact_match_only';
}
